/**
 * MCP Admin Tab - Settings Component
 *
 * Admin-only interface for managing MCP servers:
 * - Upload new MCPs (drag & drop)
 * - Test with credentials
 * - Publish MCPs (draft → available)
 * - View MCP list with status
 * - Activity/audit logs
 * - Kill switch controls (kill/quarantine/reinstate users and MCPs)
 * - Usage tracking and quotas
 *
 * Security:
 * - Feature flag: mcp_admin
 * - Role check: admin only
 * - 2FA recommended
 */

import React, { useState, useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import { authFetch, apiUrl } from "../../../utils/api";
import { useFeatureFlag, FLAGS } from "../../../config/featureFlags";
import BulkUploadZone from "./BulkUploadZone";
import CodeMirrorEditor from "./CodeMirrorEditor";
import "./MCPAdminTab.css";

const MCPAdminTab = () => {
  const { user } = useUser();
  const useCodeMirror = useFeatureFlag(FLAGS.CODEMIRROR_EDITOR);
  const [mcps, setMcps] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [testing, setTesting] = useState({});
  const [publishing, setPublishing] = useState({});

  // Test progress state
  const [testProgress, setTestProgress] = useState({ stage: "", percent: 0 });

  // Kill switch state
  const [activeKills, setActiveKills] = useState([]);
  const [activeQuarantines, setActiveQuarantines] = useState([]);
  const [killAuditLog, setKillAuditLog] = useState([]);
  const [killModalOpen, setKillModalOpen] = useState(false);
  const [killTarget, setKillTarget] = useState({ type: "", id: "", name: "" });
  const [killForm, setKillForm] = useState({ reason: "", duration_hours: "" });
  const [killLoading, setKillLoading] = useState(false);

  // Usage tracking state
  const [usageStats, setUsageStats] = useState([]);
  const [usageSummary, setUsageSummary] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [selectedUserUsage, setSelectedUserUsage] = useState(null);

  // Failures tracking state
  const [failures, setFailures] = useState([]);
  const [failureCounts, setFailureCounts] = useState([]);
  const [failuresLoading, setFailuresLoading] = useState(false);
  const [selectedFailure, setSelectedFailure] = useState(null);
  const [debuggingFailure, setDebuggingFailure] = useState(null);
  const [failureFilter, setFailureFilter] = useState("new"); // 'new', 'investigating', 'fixed', 'all'
  const [selectedFailureIds, setSelectedFailureIds] = useState(new Set()); // For bulk actions
  const [failureSearch, setFailureSearch] = useState(""); // Search filter for failures
  const [copiedSection, setCopiedSection] = useState(null); // Track which section was copied

  // Batch Analysis state (Debug Gobble Bot)
  const [analysisStatus, setAnalysisStatus] = useState(null); // null, 'available', 'unavailable'
  const [analysisRunning, setAnalysisRunning] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisRuns, setAnalysisRuns] = useState([]);
  const [selectedAnalysisRun, setSelectedAnalysisRun] = useState(null);
  const [categoryView, setCategoryView] = useState(null); // For viewing failures by category
  const [forceReanalyze, setForceReanalyze] = useState(false); // Re-analyze even previously analyzed

  // Format markdown to HTML for display
  const formatMarkdown = (text) => {
    if (!text) return "";
    let formatted = text;

    // Convert headers
    formatted = formatted.replace(
      /^### (.+)$/gm,
      '<h4 style="color:#bb86fc;margin:12px 0 8px 0;font-size:14px;">$1</h4>',
    );
    formatted = formatted.replace(
      /^## (.+)$/gm,
      '<h3 style="color:#bb86fc;margin:16px 0 10px 0;font-size:16px;">$1</h3>',
    );
    formatted = formatted.replace(
      /^# (.+)$/gm,
      '<h2 style="color:#bb86fc;margin:20px 0 12px 0;font-size:18px;">$1</h2>',
    );

    // Convert code blocks with syntax highlighting style
    formatted = formatted.replace(
      /```(\w+)?\n([\s\S]*?)```/g,
      (match, lang, code) => {
        return `<pre style="background:var(--bg-secondary);padding:12px;border-radius:6px;overflow-x:auto;margin:10px 0;border:1px solid var(--border-primary);"><code style="color:#4fc3f7;font-family:monospace;font-size:12px;">${code.trim()}</code></pre>`;
      },
    );

    // Convert inline code
    formatted = formatted.replace(
      /`([^`]+)`/g,
      '<code style="background:var(--bg-tertiary);padding:2px 6px;border-radius:3px;color:#4fc3f7;font-size:12px;">$1</code>',
    );

    // Convert bold and italic
    formatted = formatted.replace(
      /\*\*([^*]+)\*\*/g,
      '<strong style="color:#fff;">$1</strong>',
    );
    formatted = formatted.replace(/\*([^*]+)\*/g, "<em>$1</em>");

    // Convert markdown tables
    formatted = formatted.replace(
      /\|(.+)\|[\r\n]+\|[-:\s|]+\|[\r\n]+((?:\|.+\|[\r\n]*)+)/g,
      (match, header, rows) => {
        const headers = header.split("|").filter((h) => h.trim());
        const rowsArray = rows
          .trim()
          .split("\n")
          .map((row) =>
            row
              .split("|")
              .filter((cell) => cell.trim())
              .map((cell) => cell.trim()),
          );
        return `<table style="width:100%;border-collapse:collapse;margin:10px 0;font-size:12px;">
        <thead><tr>${headers.map((h) => `<th style="border:1px solid var(--border-primary);padding:8px;background:var(--bg-tertiary);text-align:left;">${h.trim()}</th>`).join("")}</tr></thead>
        <tbody>${rowsArray.map((row) => `<tr>${row.map((cell) => `<td style="border:1px solid var(--border-primary);padding:8px;">${cell}</td>`).join("")}</tr>`).join("")}</tbody>
      </table>`;
      },
    );

    // Convert lists
    formatted = formatted.replace(
      /^- (.+)$/gm,
      '<li style="margin:4px 0;margin-left:20px;">$1</li>',
    );
    formatted = formatted.replace(
      /^(\d+)\. (.+)$/gm,
      '<li style="margin:4px 0;margin-left:20px;">$2</li>',
    );

    // Convert horizontal rules
    formatted = formatted.replace(
      /^---$/gm,
      '<hr style="border:none;border-top:1px solid var(--border-primary);margin:15px 0;">',
    );

    // Convert line breaks
    formatted = formatted.replace(/\n/g, "<br>");

    return formatted;
  };

  // Copy to clipboard helper
  const copyToClipboard = async (text, section) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Copy all failure details
  const copyAllFailureDetails = () => {
    if (!selectedFailure) return;
    const details = `Tool: ${selectedFailure.tool_name}
Timestamp: ${new Date(selectedFailure.created_at).toLocaleString()}
MCP: ${selectedFailure.mcp_name || selectedFailure.mcp_id}
Error Type: ${selectedFailure.error_type}

Error Message:
${selectedFailure.error_message}

Parameters:
${typeof selectedFailure.parameters === "string" ? selectedFailure.parameters : JSON.stringify(selectedFailure.parameters, null, 2)}

${selectedFailure.stack_trace ? `Stack Trace:\n${selectedFailure.stack_trace}\n` : ""}
${selectedFailure.suggested_fix ? `Suggested Fix:\n${selectedFailure.suggested_fix}` : ""}`;

    copyToClipboard(details, "all");
  };

  // Format batch analysis report as plain text for copying
  const formatBatchAnalysisReport = () => {
    if (!analysisResult) return "";

    let report = `BATCH ANALYSIS REPORT
${"=".repeat(60)}
Generated: ${new Date().toLocaleString()}
Failures Analyzed: ${analysisResult.failuresAnalyzed}
Unique Issues: ${analysisResult.uniqueIssues}
Categories: ${Object.keys(analysisResult.categories || {}).length}
Duration: ${(analysisResult.durationMs / 1000).toFixed(1)}s

EXECUTIVE SUMMARY
${"-".repeat(40)}
${analysisResult.executiveSummary || "No summary available"}

RECOMMENDATIONS BY CATEGORY
${"-".repeat(40)}
`;

    if (analysisResult.recommendations?.length > 0) {
      for (const rec of analysisResult.recommendations) {
        report += `
[${rec.category}] ${rec.count} failures, ${rec.root_causes} root causes
${rec.recommendation}
`;
      }
    }

    // Add category breakdown
    if (analysisResult.categories) {
      report += `
CATEGORY BREAKDOWN
${"-".repeat(40)}
`;
      for (const [cat, data] of Object.entries(analysisResult.categories)) {
        report += `${cat}: ${data.count || 0} failures, ${data.rootCauses || 0} root causes\n`;
      }
    }

    return report;
  };

  // Active tab for admin sections
  const [activeAdminTab, setActiveAdminTab] = useState("mcps"); // 'mcps', 'killswitch', 'usage', 'failures'

  // Sub-tab for MCP section
  const [mcpSubTab, setMcpSubTab] = useState("single"); // 'single', 'bulk'

  // Search filter for MCPs list
  const [mcpSearch, setMcpSearch] = useState("");

  // Test results modal (persistent until closed)
  const [testResults, setTestResults] = useState({
    open: false,
    success: false,
    mcpId: null,
    mcpName: "",
    tools: [],
    error: null,
    duration: 0,
  });

  // Version history modal
  const [versionModal, setVersionModal] = useState({
    open: false,
    mcpId: null,
    mcpName: "",
    versions: [],
    loading: false,
  });

  // View code modal
  const [codeModal, setCodeModal] = useState({
    open: false,
    mcpId: null,
    mcpName: "",
    serverPy: "",
    requirements: "",
    version: null,
    lines: 0,
    loading: false,
    // Debug metadata
    uploadedBy: null,
    sourcePath: null,
    uploadedAt: null,
    // Replace files
    mcp: null,
    replaceServerPy: null,
    replaceRequirements: null,
    replacing: false,
    // Edit mode
    editing: false,
    editedServerPy: "",
    editedRequirements: "",
    saving: false,
  });

  // Upload form state
  // Category to emoji mapping
  const categoryEmojis = {
    sales: "💰",
    crm: "👥",
    marketing: "📣",
    automation: "⚡",
    analytics: "📊",
    other: "🔌",
  };

  const [uploadForm, setUploadForm] = useState({
    serverPyFile: null,
    requirementsFile: null,
    name: "",
    displayName: "",
    description: "",
    category: "sales",
    icon: "💰",
  });

  // Test credentials modal
  const [testModal, setTestModal] = useState({
    open: false,
    mcpId: null,
    mcpName: "",
    envVars: [],
    credentials: {},
  });

  // Error/success messages
  const [message, setMessage] = useState({ type: "", text: "" });

  // Check if user is admin
  const isAdmin = user?.publicMetadata?.role === "admin";

  useEffect(() => {
    if (isAdmin) {
      loadMCPs();
      loadAuditLogs();
      loadKillSwitchData();
      loadUsageSummary();
    }
  }, [isAdmin]);

  /**
   * Load MCPs from Gateway
   */
  const loadMCPs = async () => {
    try {
      console.log("[MCP Admin] Loading MCPs...");
      const response = await authFetch(apiUrl("/api/mcp/list"));
      const data = await response.json();
      console.log("[MCP Admin] MCPs loaded:", data);
      setMcps(data.mcps || []);
      setLoading(false);
    } catch (error) {
      console.error("[MCP Admin] Failed to load MCPs:", error);
      console.error("[MCP Admin] Error response:", error.response?.data);
      console.error("[MCP Admin] Error status:", error.response?.status);

      // Don't blank the screen - keep existing MCPs visible
      showMessage("error", "Failed to load MCPs");
      setLoading(false);
    }
  };

  /**
   * Load audit logs
   */
  const loadAuditLogs = async () => {
    try {
      const response = await authFetch(apiUrl("/api/mcp/audit-logs?limit=20"));
      const data = await response.json();
      setAuditLogs(data.logs || []);
    } catch (error) {
      console.error("Failed to load audit logs:", error);
    }
  };

  /**
   * Load kill switch data (active kills, quarantines, audit log)
   */
  const loadKillSwitchData = async () => {
    try {
      // Load active kills and quarantines
      const activeResponse = await authFetch(apiUrl("/api/mcp/kills/active"));
      const activeData = await activeResponse.json();
      setActiveKills(activeData.active_kills || []);
      setActiveQuarantines(activeData.active_quarantines || []);

      // Load audit log
      const auditResponse = await authFetch(
        apiUrl("/api/mcp/kills/audit?limit=50"),
      );
      const auditData = await auditResponse.json();
      setKillAuditLog(auditData.audit_log || []);
    } catch (error) {
      console.error("Failed to load kill switch data:", error);
    }
  };

  /**
   * Load usage summary
   */
  const loadUsageSummary = async () => {
    try {
      setUsageLoading(true);
      const response = await authFetch(apiUrl("/api/mcp/usage/summary"));
      const data = await response.json();
      if (data.success) {
        setUsageSummary(data);
      }
    } catch (error) {
      console.error("Failed to load usage summary:", error);
    } finally {
      setUsageLoading(false);
    }
  };

  /**
   * Load all users usage stats
   */
  const loadAllUsageStats = async () => {
    try {
      setUsageLoading(true);
      const response = await authFetch(apiUrl("/api/mcp/usage/all"));
      const data = await response.json();
      if (data.success) {
        setUsageStats(data.users || []);
      }
    } catch (error) {
      console.error("Failed to load usage stats:", error);
    } finally {
      setUsageLoading(false);
    }
  };

  /**
   * Load MCP failures for debugging (up to 500 for bulk actions)
   */
  const loadFailures = async (status = failureFilter) => {
    try {
      setFailuresLoading(true);
      // Load up to 500 failures to match batch analysis capacity
      const params = new URLSearchParams({ limit: "500" });
      if (status !== "all") {
        params.set("status", status);
      }
      const response = await authFetch(
        apiUrl(`/api/mcp/failures?${params.toString()}`),
      );
      const data = await response.json();
      setFailures(data.failures || []);
      setFailureCounts(data.counts || []);
    } catch (error) {
      console.error("Failed to load failures:", error);
    } finally {
      setFailuresLoading(false);
    }
  };

  /**
   * Check if batch analysis is available
   */
  const checkAnalysisStatus = async () => {
    try {
      const response = await authFetch(
        apiUrl("/api/mcp/failures/analysis-status"),
      );
      const data = await response.json();
      setAnalysisStatus(data.available ? "available" : "unavailable");
    } catch (error) {
      console.error("Failed to check analysis status:", error);
      setAnalysisStatus("unavailable");
    }
  };

  /**
   * Run batch analysis on failures
   */
  const [analysisProgress, setAnalysisProgress] = useState(null);

  const runBatchAnalysis = async () => {
    try {
      setAnalysisRunning(true);
      setAnalysisResult(null);
      setAnalysisProgress("Starting analysis...");

      const response = await authFetch(
        apiUrl("/api/mcp/failures/analyze-batch"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            maxFailures: 500, // Batch size - larger batches may timeout
            statusFilter: ["new", "investigating"],
            forceReanalyze, // Re-analyze even previously analyzed failures
          }),
        },
      );

      // Check for HTTP errors
      if (!response.ok) {
        const errorText = await response.text();
        console.error("[BatchAnalysis] HTTP error:", response.status, errorText);
        showMessage("error", `Server error: ${response.status} - ${errorText.substring(0, 100)}`);
        setAnalysisProgress(`Failed: HTTP ${response.status}`);
        return;
      }

      const data = await response.json();
      console.log("[BatchAnalysis] Response:", data);

      if (data.success) {
        setAnalysisResult(data);
        setAnalysisProgress(null);
        showMessage(
          "success",
          `Analyzed ${data.failuresAnalyzed} failures, found ${data.uniqueIssues} unique issues`,
        );
        // Reload failures to show updated categories
        await loadFailures();
        // Reload analysis runs
        await loadAnalysisRuns();
      } else {
        console.error("[BatchAnalysis] Failed:", data.error);
        showMessage("error", data.error || "Analysis failed (unknown error)");
        setAnalysisProgress(`Failed: ${data.error || "unknown error"}`);
      }
    } catch (error) {
      console.error("[BatchAnalysis] Exception:", error);
      showMessage("error", "Analysis failed: " + error.message);
      setAnalysisProgress(`Exception: ${error.message}`);
    } finally {
      setAnalysisRunning(false);
    }
  };

  /**
   * Load analysis run history
   */
  const loadAnalysisRuns = async () => {
    try {
      const response = await authFetch(
        apiUrl("/api/mcp/failures/analysis-runs?limit=5"),
      );
      const data = await response.json();
      setAnalysisRuns(data.runs || []);
    } catch (error) {
      console.error("Failed to load analysis runs:", error);
    }
  };

  /**
   * Load specific analysis run details
   */
  const loadAnalysisRunDetails = async (runId) => {
    try {
      const response = await authFetch(
        apiUrl(`/api/mcp/failures/analysis-runs/${runId}`),
      );
      const data = await response.json();
      setSelectedAnalysisRun(data);
    } catch (error) {
      console.error("Failed to load analysis run details:", error);
      showMessage("error", "Failed to load analysis details");
    }
  };

  /**
   * Load failures by category
   */
  const loadFailuresByCategory = async () => {
    try {
      const response = await authFetch(
        apiUrl("/api/mcp/failures/by-category"),
      );
      const data = await response.json();
      setCategoryView(data);
    } catch (error) {
      console.error("Failed to load failures by category:", error);
    }
  };

  /**
   * Run debug agent on a failure
   */
  const debugFailure = async (failureId) => {
    try {
      setDebuggingFailure(failureId);
      const response = await authFetch(
        apiUrl(`/api/mcp/failures/${failureId}/debug`),
        { method: "POST" },
      );
      const data = await response.json();
      if (data.success) {
        // Reload failures to show updated suggested_fix
        await loadFailures();
        // Select this failure to show the fix
        const updated = failures.find((f) => f.id === failureId);
        if (updated) {
          setSelectedFailure({ ...updated, suggested_fix: data.suggested_fix });
        }
        showMessage("success", "Debug analysis complete");
      } else {
        showMessage("error", data.error || "Debug failed");
      }
    } catch (error) {
      console.error("Failed to debug failure:", error);
      showMessage("error", "Debug failed: " + error.message);
    } finally {
      setDebuggingFailure(null);
    }
  };

  /**
   * Toggle selection of a failure for bulk actions
   */
  const toggleFailureSelection = (failureId, e) => {
    e.stopPropagation(); // Don't trigger row click
    setSelectedFailureIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(failureId)) {
        newSet.delete(failureId);
      } else {
        newSet.add(failureId);
      }
      return newSet;
    });
  };

  /**
   * Select/deselect all visible failures
   */
  const toggleSelectAll = () => {
    if (selectedFailureIds.size === failures.length) {
      setSelectedFailureIds(new Set());
    } else {
      setSelectedFailureIds(new Set(failures.map((f) => f.id)));
    }
  };

  /**
   * Bulk mark selected failures as fixed
   */
  const bulkMarkAsFixed = async () => {
    if (selectedFailureIds.size === 0) return;

    try {
      const response = await authFetch(
        apiUrl("/api/mcp/failures/bulk-update"),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ids: Array.from(selectedFailureIds),
            status: "fixed",
          }),
        },
      );
      const data = await response.json();
      if (data.success) {
        showMessage("success", `Marked ${data.updated} failures as fixed`);
        setSelectedFailureIds(new Set());
        setSelectedFailure(null);
        await loadFailures();
      } else {
        showMessage("error", data.error || "Bulk update failed");
      }
    } catch (error) {
      console.error("Failed to bulk update:", error);
      showMessage("error", "Bulk update failed: " + error.message);
    }
  };

  /**
   * Update failure status
   */
  const updateFailureStatus = async (failureId, newStatus) => {
    try {
      const response = await authFetch(
        apiUrl(`/api/mcp/failures/${failureId}`),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus }),
        },
      );
      const data = await response.json();
      if (data.success) {
        await loadFailures();
        showMessage("success", `Marked as ${newStatus}`);
      }
    } catch (error) {
      console.error("Failed to update failure:", error);
      showMessage("error", "Update failed");
    }
  };

  /**
   * Load usage for specific user
   */
  const loadUserUsage = async (userId) => {
    try {
      const response = await authFetch(apiUrl(`/api/mcp/usage/user/${userId}`));
      const data = await response.json();
      if (data.success) {
        setSelectedUserUsage(data);
      }
    } catch (error) {
      console.error("Failed to load user usage:", error);
    }
  };

  /**
   * Open kill modal
   */
  const openKillModal = (type, id, name) => {
    setKillTarget({ type, id, name });
    setKillForm({ reason: "", duration_hours: "" });
    setKillModalOpen(true);
  };

  /**
   * Handle kill action
   */
  const handleKillAction = async (action) => {
    if (!killForm.reason && action !== "reinstate") {
      showMessage("error", "Reason is required");
      return;
    }

    setKillLoading(true);
    try {
      const endpoint =
        killTarget.type === "user"
          ? `/api/mcp/user/${killTarget.id}/${action}`
          : `/api/mcp/mcp/${killTarget.id}/${action}`;

      const body =
        action === "reinstate"
          ? { notes: killForm.reason }
          : {
              reason: killForm.reason,
              duration_hours: killForm.duration_hours
                ? parseInt(killForm.duration_hours)
                : null,
            };

      const response = await authFetch(apiUrl(endpoint), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.success) {
        showMessage(
          "success",
          `${action === "kill" ? "Killed" : action === "quarantine" ? "Quarantined" : "Reinstated"} ${killTarget.type}: ${killTarget.name}`,
        );
        setKillModalOpen(false);
        loadKillSwitchData();
      } else {
        showMessage("error", data.error || "Action failed");
      }
    } catch (error) {
      console.error("Kill action error:", error);
      showMessage("error", error.message);
    } finally {
      setKillLoading(false);
    }
  };

  /**
   * Show message
   */
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 30000); // 30 seconds
  };

  /**
   * Handle file drop
   */
  const handleFileDrop = (e, fieldName) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      setUploadForm({ ...uploadForm, [fieldName]: file });
    }
  };

  /**
   * Handle file select
   */
  const handleFileSelect = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({ ...uploadForm, [fieldName]: file });
    }
  };

  /**
   * Handle upload
   */
  const handleUpload = async (e) => {
    e.preventDefault();

    if (
      !uploadForm.serverPyFile ||
      !uploadForm.name ||
      !uploadForm.displayName
    ) {
      showMessage("error", "Please provide server.py, name, and display name");
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("server_py", uploadForm.serverPyFile);
      if (uploadForm.requirementsFile) {
        formData.append("requirements", uploadForm.requirementsFile);
      }
      formData.append("name", uploadForm.name);
      formData.append("display_name", uploadForm.displayName);
      formData.append("description", uploadForm.description);
      formData.append("category", uploadForm.category);
      formData.append("icon", uploadForm.icon);

      const response = await authFetch(apiUrl("/api/mcp/upload"), {
        method: "POST",
        body: formData,
      });
      const data = await response.json();

      // Check if upload actually succeeded
      if (!response.ok || data.error) {
        const errorMsg =
          data.error?.issues?.join(", ") ||
          data.error ||
          data.detail?.error ||
          "Upload failed";
        showMessage("error", errorMsg);
        return;
      }

      showMessage("success", `MCP uploaded successfully! ID: ${data.id}`);

      // Reset form
      setUploadForm({
        serverPyFile: null,
        requirementsFile: null,
        name: "",
        displayName: "",
        description: "",
        category: "sales",
        icon: "🔌",
      });

      // Reload MCPs
      await loadMCPs();
      await loadAuditLogs();
    } catch (error) {
      console.error("Upload failed:", error);
      showMessage(
        "error",
        error.response?.data?.error?.issues?.join(", ") ||
          error.response?.data?.error ||
          "Upload failed",
      );
    } finally {
      setUploading(false);
    }
  };

  /**
   * Open test modal (or run directly for OAuth MCPs)
   */
  const openTestModal = (mcp) => {
    // For OAuth MCPs, skip credential modal - gateway injects token automatically
    if (mcp.auth_type === "oauth") {
      runTestDirectly(mcp);
      return;
    }

    setTestModal({
      open: true,
      mcpId: mcp.id,
      mcpName: mcp.display_name,
      envVars: mcp.env_vars_required || [],
      credentials: {},
    });
  };

  /**
   * Run test directly without credential modal (for OAuth MCPs)
   */
  const runTestDirectly = async (mcp) => {
    const mcpId = mcp.id;
    const mcpName = mcp.display_name;

    setTesting({ ...testing, [mcpId]: true });
    setTestProgress({ stage: "Starting OAuth test...", percent: 10 });

    const startTime = Date.now();

    try {
      setTestProgress({ stage: "Running test (OAuth)...", percent: 50 });

      const response = await authFetch(apiUrl(`/api/mcp/${mcpId}/test`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credentials: {} }), // Empty - gateway injects OAuth token
      });

      const data = await response.json();
      const duration = ((Date.now() - startTime) / 1000).toFixed(1);

      if (data.success) {
        setTestResults({
          open: true,
          success: true,
          mcpId,
          mcpName,
          tools: data.tools || [],
          error: null,
          duration,
        });
        await loadMCPs();
      } else {
        setTestResults({
          open: true,
          success: false,
          mcpId,
          mcpName,
          tools: [],
          error: data.error || "Test failed",
          duration,
        });
      }
    } catch (error) {
      const duration = ((Date.now() - startTime) / 1000).toFixed(1);
      setTestResults({
        open: true,
        success: false,
        mcpId,
        mcpName,
        tools: [],
        error: error.message || "Test failed",
        duration,
      });
    } finally {
      setTesting({ ...testing, [mcpId]: false });
      setTestProgress({ stage: "", percent: 0 });
    }
  };

  /**
   * Open version history modal
   */
  const openVersionHistory = async (mcp) => {
    setVersionModal({
      open: true,
      mcpId: mcp.id,
      mcpName: mcp.display_name,
      versions: [],
      loading: true,
    });

    try {
      const response = await authFetch(apiUrl(`/api/mcp/${mcp.id}/versions`));
      const data = await response.json();
      setVersionModal((prev) => ({
        ...prev,
        versions: data.versions || [],
        loading: false,
      }));
    } catch (error) {
      console.error("Failed to load version history:", error);
      showMessage("error", "Failed to load version history");
      setVersionModal((prev) => ({ ...prev, loading: false }));
    }
  };

  /**
   * View deployed code for an MCP
   */
  const openCodeViewer = async (mcp) => {
    setCodeModal({
      open: true,
      mcpId: mcp.id,
      mcpName: mcp.display_name,
      serverPy: "",
      requirements: "",
      version: null,
      lines: 0,
      loading: true,
      uploadedBy: null,
      sourcePath: null,
      uploadedAt: null,
      // Replace files
      mcp: mcp,
      replaceServerPy: null,
      replaceRequirements: null,
      replacing: false,
    });

    try {
      const response = await authFetch(apiUrl(`/api/mcp/${mcp.id}/code`));
      const data = await response.json();
      setCodeModal((prev) => ({
        ...prev,
        serverPy: data.server_py || "",
        requirements: data.requirements || "",
        version: data.current_version,
        lines: data.server_py_lines || 0,
        loading: false,
        // Debug metadata
        uploadedBy: data.uploaded_by || null,
        sourcePath: data.source_path || null,
        uploadedAt: data.uploaded_at || null,
      }));
    } catch (error) {
      console.error("Failed to load MCP code:", error);
      showMessage("error", "Failed to load MCP code");
      setCodeModal((prev) => ({ ...prev, loading: false }));
    }
  };

  /**
   * Handle rollback to previous version
   */
  const handleRollback = async (mcpId, targetVersion) => {
    if (
      !confirm(
        `Are you sure you want to rollback to version ${targetVersion}? This will clear the tool cache and require re-testing.`,
      )
    ) {
      return;
    }

    try {
      const response = await authFetch(apiUrl(`/api/mcp/${mcpId}/rollback`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          target_version: targetVersion,
          admin_user_id: user.id,
        }),
      });
      const data = await response.json();

      if (data.success) {
        showMessage("success", data.message);
        setVersionModal({ ...versionModal, open: false });
        await loadMCPs();
        await loadAuditLogs();
      }
    } catch (error) {
      console.error("Rollback failed:", error);
      showMessage("error", error.response?.data?.detail || "Rollback failed");
    }
  };

  /**
   * Enter edit mode for code
   */
  const handleCodeEdit = () => {
    setCodeModal((prev) => ({
      ...prev,
      editing: true,
      editedServerPy: prev.serverPy,
      editedRequirements: prev.requirements,
    }));
  };

  /**
   * Cancel edit mode
   */
  const handleCodeEditCancel = () => {
    setCodeModal((prev) => ({
      ...prev,
      editing: false,
      editedServerPy: "",
      editedRequirements: "",
    }));
  };

  /**
   * Save edited code (creates new version)
   */
  const handleCodeSave = async () => {
    if (!codeModal.editedServerPy.trim()) {
      showMessage("error", "Code cannot be empty");
      return;
    }

    setCodeModal((prev) => ({ ...prev, saving: true }));

    try {
      const response = await authFetch(
        apiUrl(`/api/mcp/${codeModal.mcpId}/code`),
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            server_py: codeModal.editedServerPy,
            requirements: codeModal.editedRequirements,
            changes_summary: "Edited via admin panel",
          }),
        },
      );

      const data = await response.json();
      if (data.success) {
        showMessage("success", data.message);
        setCodeModal((prev) => ({
          ...prev,
          serverPy: prev.editedServerPy,
          requirements: prev.editedRequirements,
          version: data.new_version,
          lines: prev.editedServerPy.split("\n").length,
          editing: false,
          saving: false,
          editedServerPy: "",
          editedRequirements: "",
        }));
        await loadMCPs();
        await loadAuditLogs();
      } else {
        showMessage("error", data.error || "Failed to save code");
        setCodeModal((prev) => ({ ...prev, saving: false }));
      }
    } catch (error) {
      console.error("Code save failed:", error);
      showMessage("error", "Failed to save code");
      setCodeModal((prev) => ({ ...prev, saving: false }));
    }
  };

  /**
   * Handle test with progress and persistent results
   */
  const handleTest = async () => {
    const { mcpId, credentials } = testModal;
    const mcpName = mcps.find((m) => m.id === mcpId)?.display_name || "MCP";

    console.log("🔍 Test button clicked:", {
      mcpId,
      credentials,
      userId: user?.id,
    });

    setTesting({ ...testing, [mcpId]: true });
    setTestProgress({ stage: "Initializing test...", percent: 10 });

    const startTime = Date.now();

    try {
      setTestProgress({ stage: "Starting MCP process...", percent: 30 });

      console.log("📡 Sending test request to:", `/api/mcp/${mcpId}/test`);
      const response = await authFetch(apiUrl(`/api/mcp/${mcpId}/test`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          credentials,
          admin_user_id: user?.id,
        }),
      });
      const data = await response.json();

      setTestProgress({ stage: "Discovering tools...", percent: 70 });

      console.log("✅ Test response:", data);

      const duration = Date.now() - startTime;

      if (data.success) {
        setTestProgress({ stage: "Complete!", percent: 100 });

        // Show persistent results modal
        setTestResults({
          open: true,
          success: true,
          mcpId,
          mcpName,
          tools: data.tools || [],
          error: null,
          duration,
        });

        // Close test modal
        setTestModal({ ...testModal, open: false });

        // Reload MCPs to show updated tool count
        await loadMCPs();
      } else {
        setTestResults({
          open: true,
          success: false,
          mcpId,
          mcpName,
          tools: [],
          error: data.error || "Test failed - no error details",
          duration,
        });
        setTestModal({ ...testModal, open: false });
      }
    } catch (error) {
      console.error("❌ Test failed:", error);
      console.error("Error response:", error.response?.data);

      const duration = Date.now() - startTime;

      setTestResults({
        open: true,
        success: false,
        mcpId,
        mcpName,
        tools: [],
        error:
          error.response?.data?.detail ||
          error.response?.data?.error ||
          error.message ||
          "Test failed - unknown error",
        duration,
      });

      setTestModal({ ...testModal, open: false });
    } finally {
      setTesting({ ...testing, [mcpId]: false });
      setTestProgress({ stage: "", percent: 0 });
    }
  };

  /**
   * Handle publish
   */
  const handlePublish = async (mcpId) => {
    if (
      !window.confirm("Publish this MCP? It will be available to all users.")
    ) {
      return;
    }

    setPublishing({ ...publishing, [mcpId]: true });

    try {
      const response = await authFetch(apiUrl(`/api/mcp/${mcpId}/publish`), {
        method: "POST",
      });
      const data = await response.json();
      showMessage("success", data.message || "MCP published successfully!");
      await loadMCPs();
      await loadAuditLogs();
    } catch (error) {
      console.error("Publish failed:", error);
      showMessage("error", data.error || "Upload failed");
    } finally {
      setPublishing({ ...publishing, [mcpId]: false });
    }
  };

  /**
   * Handle delete
   */
  const handleDelete = async (mcpId, mcpName) => {
    console.log(`[MCP Admin] Delete requested for MCP: ${mcpId} (${mcpName})`);

    if (!window.confirm(`Delete ${mcpName}? This cannot be undone.`)) {
      console.log(`[MCP Admin] Delete cancelled by user`);
      return;
    }

    console.log(`[MCP Admin] User confirmed delete, making API call...`);

    try {
      console.log(`[MCP Admin] DELETE /api/mcp/${mcpId}`);
      const response = await authFetch(apiUrl(`/api/mcp/${mcpId}`), {
        method: "DELETE",
      });
      const data = await response.json();
      console.log(`[MCP Admin] Delete response:`, data);

      showMessage("success", data.message || "MCP deleted successfully");

      console.log(`[MCP Admin] Reloading MCPs list...`);
      await loadMCPs();

      console.log(`[MCP Admin] Reloading audit logs...`);
      await loadAuditLogs();

      console.log(`[MCP Admin] Delete complete`);
    } catch (error) {
      console.error("[MCP Admin] Delete failed:", error);
      console.error("[MCP Admin] Error response:", error.response?.data);
      console.error("[MCP Admin] Error status:", error.response?.status);
      console.error("[MCP Admin] Error stack:", error.stack);

      const errorMessage =
        error.response?.data?.error || error.message || "Delete failed";
      showMessage("error", errorMessage);
    }
  };

  /**
   * Get status badge
   */
  const getStatusBadge = (status) => {
    const badges = {
      draft: { icon: "⚪", label: "Draft", color: "gray" },
      available: { icon: "🟢", label: "Available", color: "green" },
      error: { icon: "🔴", label: "Error", color: "red" },
      testing: { icon: "🟡", label: "Testing", color: "yellow" },
    };

    const badge = badges[status] || badges.draft;

    return (
      <span className={`mcp-status-badge mcp-status-${badge.color}`}>
        {badge.icon} {badge.label}
      </span>
    );
  };

  /**
   * Format date
   */
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Access denied
  if (!isAdmin) {
    return (
      <div className="mcp-admin-access-denied">
        <h2>🔒 Access Denied</h2>
        <p>This feature is only available to administrators.</p>
      </div>
    );
  }

  return (
    <div className="mcp-admin-tab">
      <h2>🔌 MCP Admin</h2>
      <p className="mcp-admin-description">
        Upload and manage Model Context Protocol (MCP) servers. MCPs extend
        Optimus with custom integrations.
      </p>

      {/* Admin Tab Navigation */}
      <div
        className="mcp-admin-tabs"
        style={{ display: "flex", gap: "10px", marginBottom: "20px" }}
      >
        <button
          className={`mcp-tab-btn ${activeAdminTab === "mcps" ? "active" : ""}`}
          onClick={() => setActiveAdminTab("mcps")}
          style={{
            padding: "10px 20px",
            background: activeAdminTab === "mcps" ? "#4CAF50" : "var(--bg-tertiary)",
            border: "none",
            borderRadius: "6px",
            color: activeAdminTab === "mcps" ? "#fff" : "var(--text-primary)",
            cursor: "pointer",
            fontWeight: activeAdminTab === "mcps" ? "bold" : "normal",
          }}
        >
          🔌 MCPs
        </button>
        <button
          className={`mcp-tab-btn ${activeAdminTab === "killswitch" ? "active" : ""}`}
          onClick={() => {
            setActiveAdminTab("killswitch");
            loadKillSwitchData();
          }}
          style={{
            padding: "10px 20px",
            background: activeAdminTab === "killswitch" ? "#f44336" : "var(--bg-tertiary)",
            border: "none",
            borderRadius: "6px",
            color: activeAdminTab === "killswitch" ? "#fff" : "var(--text-primary)",
            cursor: "pointer",
            fontWeight: activeAdminTab === "killswitch" ? "bold" : "normal",
          }}
        >
          🔒 Kill Switch
        </button>
        <button
          className={`mcp-tab-btn ${activeAdminTab === "usage" ? "active" : ""}`}
          onClick={() => {
            setActiveAdminTab("usage");
            loadAllUsageStats();
          }}
          style={{
            padding: "10px 20px",
            background: activeAdminTab === "usage" ? "#2196F3" : "var(--bg-tertiary)",
            border: "none",
            borderRadius: "6px",
            color: activeAdminTab === "usage" ? "#fff" : "var(--text-primary)",
            cursor: "pointer",
            fontWeight: activeAdminTab === "usage" ? "bold" : "normal",
          }}
        >
          📊 Usage
        </button>
        <button
          className={`mcp-tab-btn ${activeAdminTab === "failures" ? "active" : ""}`}
          onClick={() => {
            setActiveAdminTab("failures");
            loadFailures();
            checkAnalysisStatus();
          }}
          style={{
            padding: "10px 20px",
            background: activeAdminTab === "failures" ? "#ff9800" : "var(--bg-tertiary)",
            border: "none",
            borderRadius: "6px",
            color: activeAdminTab === "failures" ? "#fff" : "var(--text-primary)",
            cursor: "pointer",
            fontWeight: activeAdminTab === "failures" ? "bold" : "normal",
          }}
        >
          🐛 Failures
        </button>
      </div>

      {/* Message banner */}
      {message.text && (
        <div className={`mcp-message mcp-message-${message.type}`}>
          {message.type === "success" ? "✅" : "❌"} {message.text}
        </div>
      )}

      {/* ============================================ */}
      {/* KILL SWITCH TAB */}
      {/* ============================================ */}
      {activeAdminTab === "killswitch" && (
        <div className="mcp-admin-section">
          <h3>🔒 Kill Switch Controls</h3>
          <p style={{ color: "var(--text-secondary)", marginBottom: "20px" }}>
            Emergency controls to disable users or MCPs. Use with caution.
          </p>

          {/* Quick Actions */}
          <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
            <button
              onClick={() => openKillModal("user", "", "Enter User ID")}
              style={{
                padding: "10px 15px",
                background: "var(--accent-danger)",
                border: "none",
                borderRadius: "6px",
                color: "var(--text-primary)",
                cursor: "pointer",
              }}
            >
              🔴 Kill User
            </button>
            <button
              onClick={() => openKillModal("user", "", "Enter User ID")}
              style={{
                padding: "10px 15px",
                background: "var(--accent-warning)",
                border: "none",
                borderRadius: "6px",
                color: "var(--text-primary)",
                cursor: "pointer",
              }}
            >
              ⚠️ Quarantine User
            </button>
          </div>

          {/* Active Kills */}
          <div style={{ marginBottom: "30px" }}>
            <h4 style={{ color: "var(--accent-danger)", marginBottom: "10px" }}>
              🔴 Active Kills ({activeKills.length})
            </h4>
            {activeKills.length === 0 ? (
              <p style={{ color: "var(--text-tertiary)" }}>No active kills</p>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                {activeKills.map((kill, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: "var(--bg-danger-subtle)",
                      border: "1px solid #d32f2f",
                      borderRadius: "8px",
                      padding: "15px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <strong style={{ color: "var(--accent-danger)" }}>
                          {kill.scope === "user" ? "👤 User" : "🔌 MCP"}:{" "}
                          {kill.user_id || kill.mcp_id}
                        </strong>
                        <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
                          Reason: {kill.reason}
                        </p>
                        <p style={{ color: "var(--text-tertiary)", fontSize: "12px" }}>
                          Killed by {kill.admin_id} at{" "}
                          {formatDate(kill.created_at)}
                          {kill.expires_at &&
                            ` • Expires: ${formatDate(kill.expires_at)}`}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setKillTarget({
                            type: kill.scope === "user" ? "user" : "mcp",
                            id: kill.user_id || kill.mcp_id,
                            name: kill.user_id || kill.mcp_id,
                          });
                          handleKillAction("reinstate");
                        }}
                        style={{
                          padding: "8px 12px",
                          background: "var(--accent-success)",
                          border: "none",
                          borderRadius: "4px",
                          color: "var(--text-primary)",
                          cursor: "pointer",
                        }}
                      >
                        ✅ Reinstate
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Active Quarantines */}
          <div style={{ marginBottom: "30px" }}>
            <h4 style={{ color: "var(--accent-warning)", marginBottom: "10px" }}>
              ⚠️ Active Quarantines ({activeQuarantines.length})
            </h4>
            {activeQuarantines.length === 0 ? (
              <p style={{ color: "var(--text-tertiary)" }}>No active quarantines</p>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                {activeQuarantines.map((q, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: "var(--bg-warning-subtle)",
                      border: "1px solid #ff9800",
                      borderRadius: "8px",
                      padding: "15px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <strong style={{ color: "var(--accent-warning)" }}>
                          👤 User: {q.user_id}
                        </strong>
                        <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
                          Reason: {q.reason}
                        </p>
                        <p style={{ color: "var(--text-tertiary)", fontSize: "12px" }}>
                          Quarantined by {q.admin_id} at{" "}
                          {formatDate(q.created_at)}
                        </p>
                      </div>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <button
                          onClick={() =>
                            openKillModal("user", q.user_id, q.user_id)
                          }
                          style={{
                            padding: "8px 12px",
                            background: "var(--accent-danger)",
                            border: "none",
                            borderRadius: "4px",
                            color: "var(--text-primary)",
                            cursor: "pointer",
                          }}
                        >
                          🔴 Upgrade to Kill
                        </button>
                        <button
                          onClick={() => {
                            setKillTarget({
                              type: "user",
                              id: q.user_id,
                              name: q.user_id,
                            });
                            handleKillAction("reinstate");
                          }}
                          style={{
                            padding: "8px 12px",
                            background: "var(--accent-success)",
                            border: "none",
                            borderRadius: "4px",
                            color: "var(--text-primary)",
                            cursor: "pointer",
                          }}
                        >
                          ✅ Reinstate
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Kill Audit Log */}
          <div>
            <h4 style={{ marginBottom: "10px" }}>📜 Kill Switch Audit Log</h4>
            {killAuditLog.length === 0 ? (
              <p style={{ color: "var(--text-tertiary)" }}>No kill switch actions yet</p>
            ) : (
              <div
                style={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  background: "var(--bg-secondary)",
                  borderRadius: "8px",
                  padding: "10px",
                }}
              >
                {killAuditLog.map((log, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: "10px",
                      borderBottom:
                        idx < killAuditLog.length - 1
                          ? "1px solid var(--border-primary)"
                          : "none",
                    }}
                  >
                    <span
                      style={{
                        color:
                          log.action === "kill"
                            ? "#f44336"
                            : log.action === "quarantine"
                              ? "#ff9800"
                              : "#4CAF50",
                        fontWeight: "bold",
                      }}
                    >
                      {log.action.toUpperCase()}
                    </span>
                    <span style={{ color: "var(--text-secondary)", marginLeft: "10px" }}>
                      {log.scope}: {log.user_id || log.mcp_id}
                    </span>
                    <span
                      style={{
                        color: "var(--text-tertiary)",
                        marginLeft: "10px",
                        fontSize: "12px",
                      }}
                    >
                      by {log.admin_id} • {formatDate(log.timestamp)}
                    </span>
                    {log.reason && (
                      <p
                        style={{
                          color: "#888",
                          margin: "5px 0 0 0",
                          fontSize: "13px",
                        }}
                      >
                        {log.reason}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* USAGE TAB */}
      {/* ============================================ */}
      {activeAdminTab === "usage" && (
        <div className="mcp-admin-section">
          <h3>📊 Usage Tracking</h3>
          <p style={{ color: "var(--text-secondary)", marginBottom: "20px" }}>
            Monitor system usage, costs, and identify heavy users.
          </p>

          {/* Summary Cards */}
          {usageSummary && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "15px",
                marginBottom: "30px",
              }}
            >
              <div
                style={{
                  background: "var(--bg-success-subtle)",
                  border: "1px solid #4CAF50",
                  borderRadius: "8px",
                  padding: "20px",
                }}
              >
                <h4 style={{ color: "var(--accent-success)", margin: "0 0 10px 0" }}>
                  Total Users
                </h4>
                <p style={{ fontSize: "24px", fontWeight: "bold", margin: 0 }}>
                  {usageSummary.system?.total_users || 0}
                </p>
              </div>
              <div
                style={{
                  background: "var(--bg-accent-subtle)",
                  border: "1px solid #2196F3",
                  borderRadius: "8px",
                  padding: "20px",
                }}
              >
                <h4 style={{ color: "var(--accent-primary)", margin: "0 0 10px 0" }}>
                  Total Requests
                </h4>
                <p style={{ fontSize: "24px", fontWeight: "bold", margin: 0 }}>
                  {usageSummary.system?.total_requests?.toLocaleString() || 0}
                </p>
              </div>
              <div
                style={{
                  background: "var(--bg-warning-subtle)",
                  border: "1px solid #ff9800",
                  borderRadius: "8px",
                  padding: "20px",
                }}
              >
                <h4 style={{ color: "var(--accent-warning)", margin: "0 0 10px 0" }}>
                  Total Cost
                </h4>
                <p style={{ fontSize: "24px", fontWeight: "bold", margin: 0 }}>
                  ${(usageSummary.system?.total_cost || 0).toFixed(2)}
                </p>
              </div>
              <div
                style={{
                  background: "var(--bg-tertiary)",
                  border: "1px solid #9c27b0",
                  borderRadius: "8px",
                  padding: "20px",
                }}
              >
                <h4 style={{ color: "var(--accent-primary)", margin: "0 0 10px 0" }}>
                  Active Automations
                </h4>
                <p style={{ fontSize: "24px", fontWeight: "bold", margin: 0 }}>
                  {usageSummary.automations?.active_automations || 0}
                </p>
              </div>
            </div>
          )}

          {/* Time-based stats */}
          {usageSummary && (
            <div
              style={{
                display: "flex",
                gap: "20px",
                marginBottom: "30px",
              }}
            >
              <div
                style={{
                  flex: 1,
                  background: "var(--bg-secondary)",
                  borderRadius: "8px",
                  padding: "15px",
                }}
              >
                <h4 style={{ margin: "0 0 10px 0" }}>Last 24 Hours</h4>
                <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
                  Active Users: {usageSummary.last_24h?.active_users || 0}
                </p>
                <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
                  Requests: {usageSummary.last_24h?.requests || 0}
                </p>
                <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
                  Cost: ${(usageSummary.last_24h?.cost || 0).toFixed(2)}
                </p>
              </div>
              <div
                style={{
                  flex: 1,
                  background: "var(--bg-secondary)",
                  borderRadius: "8px",
                  padding: "15px",
                }}
              >
                <h4 style={{ margin: "0 0 10px 0" }}>Last 7 Days</h4>
                <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
                  Active Users: {usageSummary.last_7d?.active_users || 0}
                </p>
                <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
                  Requests: {usageSummary.last_7d?.requests || 0}
                </p>
                <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
                  Cost: ${(usageSummary.last_7d?.cost || 0).toFixed(2)}
                </p>
              </div>
            </div>
          )}

          {/* User Usage Table */}
          <h4 style={{ marginBottom: "10px" }}>👥 Top 10 Users by Requests</h4>
          {usageLoading ? (
            <p style={{ color: "var(--text-tertiary)" }}>Loading usage data...</p>
          ) : usageStats.length === 0 ? (
            <p style={{ color: "var(--text-tertiary)" }}>No usage data available</p>
          ) : (
            <div
              style={{
                maxHeight: "400px",
                overflowY: "auto",
                background: "var(--bg-secondary)",
                borderRadius: "8px",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--bg-tertiary)" }}>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        borderBottom: "1px solid var(--border-primary)",
                      }}
                    >
                      User ID
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        borderBottom: "1px solid var(--border-primary)",
                      }}
                    >
                      Requests
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        borderBottom: "1px solid var(--border-primary)",
                      }}
                    >
                      Tokens
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        borderBottom: "1px solid var(--border-primary)",
                      }}
                    >
                      Cost
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        borderBottom: "1px solid var(--border-primary)",
                      }}
                    >
                      Automations
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        borderBottom: "1px solid var(--border-primary)",
                      }}
                    >
                      Last Active
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        borderBottom: "1px solid var(--border-primary)",
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {usageStats.map((stat, idx) => (
                    <tr
                      key={idx}
                      style={{
                        borderBottom: "1px solid var(--border-primary)",
                        background:
                          stat.total_cost > 10 ? "#2a1a1a" : "transparent",
                      }}
                    >
                      <td style={{ padding: "12px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontSize: "12px",
                            }}
                            title={stat.user_id}
                          >
                            {stat.user_id?.substring(0, 20)}...
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(stat.user_id);
                              showMessage("success", "User ID copied!");
                            }}
                            style={{
                              padding: "2px 6px",
                              background: "var(--border-primary)",
                              border: "1px solid #555",
                              borderRadius: "4px",
                              color: "var(--text-secondary)",
                              cursor: "pointer",
                              fontSize: "10px",
                            }}
                            title="Copy full user ID"
                          >
                            📋
                          </button>
                        </div>
                      </td>
                      <td style={{ padding: "12px", textAlign: "right" }}>
                        {stat.total_requests?.toLocaleString() || 0}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          fontSize: "12px",
                        }}
                      >
                        {(
                          (stat.total_input_tokens || 0) +
                          (stat.total_output_tokens || 0)
                        ).toLocaleString()}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          color:
                            stat.total_cost > 5
                              ? "#f44336"
                              : stat.total_cost > 1
                                ? "#ff9800"
                                : "#4CAF50",
                        }}
                      >
                        ${(stat.total_cost || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: "12px", textAlign: "right" }}>
                        {stat.active_automations || 0} /{" "}
                        {stat.automation_count || 0}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          fontSize: "12px",
                          color: "#888",
                        }}
                      >
                        {stat.last_activity
                          ? new Date(stat.last_activity).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        <button
                          onClick={() =>
                            openKillModal("user", stat.user_id, stat.user_id)
                          }
                          style={{
                            padding: "4px 8px",
                            background: "var(--accent-danger)",
                            border: "none",
                            borderRadius: "4px",
                            color: "var(--text-primary)",
                            cursor: "pointer",
                            fontSize: "11px",
                          }}
                          title="Kill user"
                        >
                          🔴
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* FAILURES TAB */}
      {/* ============================================ */}
      {activeAdminTab === "failures" && (
        <div className="mcp-admin-section">
          <h3>🐛 MCP Failures</h3>
          <p style={{ color: "var(--text-secondary)", marginBottom: "20px" }}>
            Tool errors from all users. Debug and fix issues here.
          </p>

          {/* Search Bar */}
          <div style={{ marginBottom: "15px" }}>
            <input
              type="text"
              placeholder="🔍 Search by tool name, MCP, or error message..."
              value={failureSearch}
              onChange={(e) => setFailureSearch(e.target.value)}
              style={{
                width: "100%",
                maxWidth: "400px",
                padding: "10px 15px",
                background: "var(--bg-tertiary)",
                border: "1px solid var(--border-primary)",
                borderRadius: "6px",
                color: "var(--text-primary)",
                fontSize: "14px",
              }}
            />
          </div>

          {/* Status Filter */}
          <div
            style={{
              display: "flex",
              gap: "10px",
              marginBottom: "20px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {["new", "investigating", "fixed", "all"].map((status) => (
              <button
                key={status}
                onClick={() => {
                  setFailureFilter(status);
                  setSelectedFailureIds(new Set()); // Clear selection on filter change
                  loadFailures(status);
                }}
                style={{
                  padding: "8px 16px",
                  background: failureFilter === status ? "#ff9800" : "var(--bg-tertiary)",
                  border: "none",
                  borderRadius: "6px",
                  color: "var(--text-primary)",
                  cursor: "pointer",
                  textTransform: "capitalize",
                }}
              >
                {status}{" "}
                {failureCounts.find((c) => c.status === status)?.count
                  ? `(${failureCounts.find((c) => c.status === status).count})`
                  : ""}
              </button>
            ))}

            {/* Bulk Actions */}
            {failures.length > 0 && (
              <div
                style={{
                  marginLeft: "auto",
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                <button
                  onClick={toggleSelectAll}
                  style={{
                    padding: "6px 12px",
                    background: "var(--bg-tertiary)",
                    border: "none",
                    borderRadius: "4px",
                    color: "var(--text-primary)",
                    cursor: "pointer",
                    fontSize: "13px",
                  }}
                >
                  {selectedFailureIds.size === failures.length && failures.length > 0
                    ? "Deselect All"
                    : `Select All ${failures.length > 0 ? `(${failures.length})` : ""}`}
                </button>
                {selectedFailureIds.size > 0 && (
                  <button
                    onClick={bulkMarkAsFixed}
                    style={{
                      padding: "6px 12px",
                      background: "var(--accent-success)",
                      border: "none",
                      borderRadius: "4px",
                      color: "var(--text-primary)",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: "bold",
                    }}
                  >
                    ✓ Mark {selectedFailureIds.size} as Fixed
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Batch Analysis Section */}
          <div
            style={{
              background: "var(--bg-secondary)",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
              border: "1px solid var(--border-primary)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: analysisResult ? "15px" : "0",
              }}
            >
              <div>
                <h4 style={{ margin: "0 0 5px 0", color: "var(--accent-primary)" }}>
                  Batch Analysis (Debug Gobble Bot)
                </h4>
                <p style={{ margin: 0, fontSize: "12px", color: "var(--text-secondary)" }}>
                  Analyze all failures at once using AI to categorize issues and
                  suggest fixes
                </p>
              </div>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "12px",
                    color: "var(--text-secondary)",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={forceReanalyze}
                    onChange={(e) => setForceReanalyze(e.target.checked)}
                    style={{ cursor: "pointer" }}
                  />
                  Re-analyze all
                </label>
                <button
                  onClick={runBatchAnalysis}
                  disabled={analysisRunning || analysisStatus === "unavailable"}
                  style={{
                    padding: "8px 16px",
                    background: analysisRunning ? "#555" : "#9c27b0",
                    border: "none",
                    borderRadius: "6px",
                    color: "var(--text-primary)",
                    cursor: analysisRunning ? "wait" : "pointer",
                    fontSize: "13px",
                    fontWeight: "bold",
                  }}
                >
                  {analysisRunning ? "Analyzing..." : "Analyze All Failures"}
                </button>
              </div>
            </div>

            {/* Progress Indicator */}
            {analysisProgress && (
              <div
                style={{
                  background: "var(--bg-tertiary)",
                  borderRadius: "6px",
                  padding: "12px",
                  marginTop: "10px",
                  color: analysisProgress.startsWith("Failed") || analysisProgress.startsWith("Exception") ? "#ff6b6b" : "#9c27b0",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {!analysisProgress.startsWith("Failed") && !analysisProgress.startsWith("Exception") && (
                  <span style={{ animation: "spin 1s linear infinite" }}>⏳</span>
                )}
                {analysisProgress}
              </div>
            )}

            {/* Analysis Result */}
            {analysisResult && (
              <div
                style={{
                  background: "var(--bg-tertiary)",
                  borderRadius: "6px",
                  padding: "12px",
                  marginTop: "10px",
                }}
              >
                {/* Copy Report Button */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: "8px",
                  }}
                >
                  <button
                    onClick={() =>
                      copyToClipboard(formatBatchAnalysisReport(), "batchReport")
                    }
                    style={{
                      padding: "4px 10px",
                      background:
                        copiedSection === "batchReport"
                          ? "#4CAF50"
                          : "transparent",
                      border: "1px solid var(--border-primary)",
                      borderRadius: "4px",
                      color: copiedSection === "batchReport" ? "#fff" : "#8e8e93",
                      cursor: "pointer",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    {copiedSection === "batchReport" ? "Copied!" : "Copy Report"}
                  </button>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                    gap: "10px",
                    marginBottom: "10px",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#4fc3f7",
                      }}
                    >
                      {analysisResult.failuresAnalyzed}
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                      Analyzed
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "var(--accent-warning)",
                      }}
                    >
                      {analysisResult.uniqueIssues}
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                      Unique Issues
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "var(--accent-success)",
                      }}
                    >
                      {Object.keys(analysisResult.categories || {}).length}
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                      Categories
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "var(--accent-primary)",
                      }}
                    >
                      {(analysisResult.durationMs / 1000).toFixed(1)}s
                    </div>
                    <div style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                      Duration
                    </div>
                  </div>
                </div>

                {analysisResult.executiveSummary && (
                  <div
                    style={{
                      padding: "10px",
                      background: "var(--bg-secondary)",
                      borderRadius: "4px",
                      fontSize: "13px",
                      color: "var(--text-secondary)",
                      marginBottom: "10px",
                    }}
                  >
                    {analysisResult.executiveSummary}
                  </div>
                )}

                {analysisResult.recommendations?.length > 0 && (
                  <div>
                    <h5
                      style={{ margin: "10px 0 8px 0", color: "var(--accent-primary)" }}
                    >
                      Recommendations
                    </h5>
                    {analysisResult.recommendations.map((rec, i) => (
                      <div
                        key={i}
                        style={{
                          padding: "8px 12px",
                          background: "var(--bg-secondary)",
                          borderRadius: "4px",
                          marginBottom: "6px",
                          borderLeft: `3px solid ${
                            rec.category === "QUICK_WINS"
                              ? "#4CAF50"
                              : rec.category === "AUTH"
                                ? "#f44336"
                                : rec.category === "TIMEOUT"
                                  ? "#ff9800"
                                  : rec.category === "CODE_BUG"
                                    ? "#e91e63"
                                    : "#9c27b0"
                          }`,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "4px",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: "bold",
                              fontSize: "12px",
                              color: "var(--text-primary)",
                            }}
                          >
                            {rec.category}
                          </span>
                          <span style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                            {rec.count} failures, {rec.root_causes} root causes
                          </span>
                        </div>
                        <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
                          {rec.recommendation}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Category View */}
            {categoryView && (
              <div
                style={{
                  background: "var(--bg-tertiary)",
                  borderRadius: "6px",
                  padding: "12px",
                  marginTop: "10px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <h5 style={{ margin: 0, color: "var(--accent-primary)" }}>
                    Failures by Category
                  </h5>
                  <button
                    onClick={() => setCategoryView(null)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "var(--text-secondary)",
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                  >
                    ×
                  </button>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: "8px",
                  }}
                >
                  {categoryView.categories?.map((cat) => (
                    <div
                      key={cat.category}
                      style={{
                        padding: "10px",
                        background: "var(--bg-secondary)",
                        borderRadius: "4px",
                        borderLeft: `3px solid ${
                          cat.category === "AUTH"
                            ? "#f44336"
                            : cat.category === "TIMEOUT"
                              ? "#ff9800"
                              : cat.category === "CODE_BUG"
                                ? "#e91e63"
                                : cat.category === "RATE_LIMIT"
                                  ? "#ff5722"
                                  : cat.category === "PARAM_ERROR"
                                    ? "#2196F3"
                                    : cat.category === "EXTERNAL_API"
                                      ? "#9c27b0"
                                      : "#666"
                        }`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          fontWeight: "bold",
                          color: "var(--text-primary)",
                        }}
                      >
                        {cat.category || "UNKNOWN"}
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "var(--text-secondary)",
                          marginTop: "4px",
                        }}
                      >
                        {categoryView.labels?.[cat.category] ||
                          "Uncategorized"}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginTop: "6px",
                          fontSize: "11px",
                        }}
                      >
                        <span style={{ color: "var(--accent-danger)" }}>
                          {cat.new_count} new
                        </span>
                        <span style={{ color: "var(--accent-warning)" }}>
                          {cat.investigating_count} investigating
                        </span>
                        <span style={{ color: "var(--accent-success)" }}>
                          {cat.fixed_count} fixed
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "bold",
                          color: "#4fc3f7",
                          marginTop: "4px",
                        }}
                      >
                        {cat.count} total
                      </div>
                    </div>
                  ))}
                </div>

                {/* Recent Analysis Runs */}
                {analysisRuns.length > 0 && (
                  <div style={{ marginTop: "15px" }}>
                    <h5 style={{ margin: "0 0 8px 0", color: "var(--accent-primary)" }}>
                      Recent Analysis Runs
                    </h5>
                    {analysisRuns.map((run) => (
                      <div
                        key={run.id}
                        onClick={() => loadAnalysisRunDetails(run.id)}
                        style={{
                          padding: "8px 12px",
                          background:
                            selectedAnalysisRun?.id === run.id
                              ? "#3a3a3a"
                              : "var(--bg-secondary)",
                          borderRadius: "4px",
                          marginBottom: "6px",
                          cursor: "pointer",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <span style={{ fontSize: "12px", color: "var(--text-primary)" }}>
                            {new Date(run.created_at).toLocaleString()}
                          </span>
                          <span
                            style={{
                              fontSize: "11px",
                              color: "var(--text-secondary)",
                              marginLeft: "10px",
                            }}
                          >
                            by {run.triggered_by?.slice(0, 15)}...
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: "10px" }}>
                          <span
                            style={{
                              fontSize: "11px",
                              color:
                                run.status === "completed"
                                  ? "#4CAF50"
                                  : run.status === "running"
                                    ? "#ff9800"
                                    : "#f44336",
                            }}
                          >
                            {run.status}
                          </span>
                          <span style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                            {run.failures_analyzed} analyzed
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Selected Analysis Run Details */}
                {selectedAnalysisRun && (
                  <div
                    style={{
                      marginTop: "15px",
                      padding: "12px",
                      background: "var(--bg-secondary)",
                      borderRadius: "6px",
                      border: "1px solid #3a3a3a",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "10px",
                      }}
                    >
                      <h5 style={{ margin: 0, color: "var(--accent-primary)" }}>
                        Analysis Run Details
                      </h5>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          onClick={() => {
                            const report = `ANALYSIS RUN REPORT
${"=".repeat(50)}
ID: ${selectedAnalysisRun.id}
Date: ${new Date(selectedAnalysisRun.created_at).toLocaleString()}
Status: ${selectedAnalysisRun.status}
Failures Analyzed: ${selectedAnalysisRun.failures_analyzed}
Unique Issues: ${selectedAnalysisRun.unique_issues || "N/A"}
Duration: ${selectedAnalysisRun.duration_ms ? (selectedAnalysisRun.duration_ms / 1000).toFixed(1) + "s" : "N/A"}
Model: ${selectedAnalysisRun.model_used || "N/A"}

EXECUTIVE SUMMARY
${"-".repeat(40)}
${selectedAnalysisRun.executive_summary || "No summary available"}

RECOMMENDATIONS
${"-".repeat(40)}
${(selectedAnalysisRun.recommendations || [])
  .map((r, i) => `${i + 1}. [${r.category}] ${r.count} failures\n   ${r.recommendation}`)
  .join("\n\n") || "No recommendations"}
`;
                            copyToClipboard(report, "analysisRunReport");
                          }}
                          style={{
                            padding: "4px 10px",
                            background:
                              copiedSection === "analysisRunReport"
                                ? "#4CAF50"
                                : "transparent",
                            border: "1px solid var(--border-primary)",
                            borderRadius: "4px",
                            color:
                              copiedSection === "analysisRunReport"
                                ? "#fff"
                                : "#8e8e93",
                            cursor: "pointer",
                            fontSize: "11px",
                          }}
                        >
                          {copiedSection === "analysisRunReport"
                            ? "Copied!"
                            : "Copy"}
                        </button>
                        <button
                          onClick={() => setSelectedAnalysisRun(null)}
                          style={{
                            padding: "4px 8px",
                            background: "transparent",
                            border: "1px solid var(--border-primary)",
                            borderRadius: "4px",
                            color: "var(--text-secondary)",
                            cursor: "pointer",
                            fontSize: "11px",
                          }}
                        >
                          ×
                        </button>
                      </div>
                    </div>

                    {/* Summary stats */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(4, 1fr)",
                        gap: "10px",
                        marginBottom: "12px",
                      }}
                    >
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                            color: "#4fc3f7",
                          }}
                        >
                          {selectedAnalysisRun.failures_analyzed || 0}
                        </div>
                        <div style={{ fontSize: "10px", color: "var(--text-secondary)" }}>
                          Analyzed
                        </div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                            color: "var(--accent-warning)",
                          }}
                        >
                          {selectedAnalysisRun.unique_issues || 0}
                        </div>
                        <div style={{ fontSize: "10px", color: "var(--text-secondary)" }}>
                          Issues
                        </div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                            color:
                              selectedAnalysisRun.status === "completed"
                                ? "#4CAF50"
                                : selectedAnalysisRun.status === "failed"
                                  ? "#f44336"
                                  : "#ff9800",
                          }}
                        >
                          {selectedAnalysisRun.status}
                        </div>
                        <div style={{ fontSize: "10px", color: "var(--text-secondary)" }}>
                          Status
                        </div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                            color: "var(--accent-primary)",
                          }}
                        >
                          {selectedAnalysisRun.duration_ms
                            ? (selectedAnalysisRun.duration_ms / 1000).toFixed(1) + "s"
                            : "N/A"}
                        </div>
                        <div style={{ fontSize: "10px", color: "var(--text-secondary)" }}>
                          Duration
                        </div>
                      </div>
                    </div>

                    {/* Model used */}
                    {selectedAnalysisRun.model_used && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "var(--text-secondary)",
                          marginBottom: "10px",
                        }}
                      >
                        Model: {selectedAnalysisRun.model_used}
                      </div>
                    )}

                    {/* Executive summary */}
                    {selectedAnalysisRun.executive_summary && (
                      <div
                        style={{
                          padding: "10px",
                          background: "var(--bg-tertiary)",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "var(--text-secondary)",
                          marginBottom: "12px",
                        }}
                      >
                        {selectedAnalysisRun.executive_summary}
                      </div>
                    )}

                    {/* Error message if failed */}
                    {selectedAnalysisRun.error_message && (
                      <div
                        style={{
                          padding: "10px",
                          background: "var(--bg-danger-subtle)",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: "var(--accent-danger)",
                          marginBottom: "12px",
                        }}
                      >
                        Error: {selectedAnalysisRun.error_message}
                      </div>
                    )}

                    {/* Recommendations */}
                    {selectedAnalysisRun.recommendations?.length > 0 && (
                      <div>
                        <h6
                          style={{
                            margin: "0 0 8px 0",
                            color: "var(--accent-primary)",
                            fontSize: "12px",
                          }}
                        >
                          Recommendations ({selectedAnalysisRun.recommendations.length})
                        </h6>
                        <div
                          style={{
                            maxHeight: "200px",
                            overflowY: "auto",
                          }}
                        >
                          {selectedAnalysisRun.recommendations.map((rec, i) => (
                            <div
                              key={i}
                              style={{
                                padding: "8px 10px",
                                background: "var(--bg-tertiary)",
                                borderRadius: "4px",
                                marginBottom: "6px",
                                borderLeft: `3px solid ${
                                  rec.category === "QUICK_WINS"
                                    ? "#4CAF50"
                                    : rec.category === "AUTH"
                                      ? "#f44336"
                                      : rec.category === "TIMEOUT"
                                        ? "#ff9800"
                                        : rec.category === "CODE_BUG"
                                          ? "#e91e63"
                                          : rec.category === "EXTERNAL_API"
                                            ? "#2196F3"
                                            : "#9c27b0"
                                }`,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  marginBottom: "4px",
                                }}
                              >
                                <span
                                  style={{
                                    fontWeight: "bold",
                                    fontSize: "11px",
                                    color: "var(--text-primary)",
                                  }}
                                >
                                  {rec.category}
                                </span>
                                <span
                                  style={{ fontSize: "10px", color: "var(--text-secondary)" }}
                                >
                                  {rec.count} failures
                                  {rec.fix_location && ` • ${rec.fix_location}`}
                                </span>
                              </div>
                              <div style={{ fontSize: "11px", color: "var(--text-secondary)" }}>
                                {rec.recommendation}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Categories breakdown */}
                    {selectedAnalysisRun.categories_found && (
                      <div style={{ marginTop: "12px" }}>
                        <h6
                          style={{
                            margin: "0 0 8px 0",
                            color: "var(--accent-primary)",
                            fontSize: "12px",
                          }}
                        >
                          Categories
                        </h6>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                          }}
                        >
                          {Object.entries(selectedAnalysisRun.categories_found).map(
                            ([cat, data]) => (
                              <span
                                key={cat}
                                style={{
                                  padding: "4px 8px",
                                  background: "var(--bg-tertiary)",
                                  borderRadius: "4px",
                                  fontSize: "10px",
                                  color: "var(--text-secondary)",
                                }}
                              >
                                {cat}: {data.count || data}
                              </span>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Failures List */}
          {failuresLoading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              Loading failures...
            </div>
          ) : failures.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "40px", color: "var(--text-secondary)" }}
            >
              🎉 No failures to show
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                gap: "20px",
                overflowX: "auto",
                minWidth: 0,
              }}
            >
              {/* Failures List */}
              <div
                style={{
                  flex: "1",
                  minWidth: "280px",
                  maxHeight: "600px",
                  overflowY: "auto",
                }}
              >
                {failures
                  .filter((failure) => {
                    if (!failureSearch.trim()) return true;
                    const search = failureSearch.toLowerCase();
                    return (
                      (failure.tool_name || "")
                        .toLowerCase()
                        .includes(search) ||
                      (failure.mcp_name || "").toLowerCase().includes(search) ||
                      (failure.mcp_id || "").toLowerCase().includes(search) ||
                      (failure.error_message || "")
                        .toLowerCase()
                        .includes(search)
                    );
                  })
                  .map((failure) => (
                    <div
                      key={failure.id}
                      onClick={() => setSelectedFailure(failure)}
                      style={{
                        padding: "15px",
                        paddingLeft: "10px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        background: selectedFailureIds.has(failure.id)
                          ? "#3d3a2a"
                          : selectedFailure?.id === failure.id
                            ? "#3a3a3a"
                            : "var(--bg-tertiary)",
                        borderRadius: "8px",
                        marginBottom: "10px",
                        cursor: "pointer",
                        borderLeft: `4px solid ${
                          failure.status === "new"
                            ? "#f44336"
                            : failure.status === "investigating"
                              ? "#ff9800"
                              : "#4CAF50"
                        }`,
                      }}
                    >
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedFailureIds.has(failure.id)}
                        onChange={(e) => toggleFailureSelection(failure.id, e)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                          marginTop: "2px",
                          accentColor: "#4CAF50",
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <strong style={{ color: "var(--text-primary)" }}>
                            {failure.mcp_name || failure.mcp_id}
                          </strong>
                          <span
                            style={{
                              fontSize: "12px",
                              color: "var(--text-secondary)",
                            }}
                          >
                            {new Date(failure.created_at).toLocaleString([], {
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                            })}
                          </span>
                        </div>
                        <div style={{ color: "var(--accent-danger)", fontSize: "14px" }}>
                          {failure.tool_name}
                        </div>
                        <div
                          style={{
                            color: "var(--text-secondary)",
                            fontSize: "13px",
                            marginTop: "5px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {failure.error_message?.substring(0, 80)}...
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              {/* Failure Detail Panel */}
              {selectedFailure && (
                <div
                  style={{
                    flex: "1.5",
                    minWidth: "400px",
                    background: "var(--bg-secondary)",
                    borderRadius: "8px",
                    padding: "20px",
                    maxHeight: "600px",
                    overflowY: "auto",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "15px",
                    }}
                  >
                    <h4 style={{ margin: 0, color: "var(--text-primary)" }}>
                      {selectedFailure.tool_name}
                    </h4>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={copyAllFailureDetails}
                        style={{
                          padding: "6px 12px",
                          background:
                            copiedSection === "all" ? "#4CAF50" : "#3a3a3a",
                          border: "none",
                          borderRadius: "4px",
                          color: "var(--text-primary)",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        {copiedSection === "all" ? "✓ Copied!" : "📋 Copy All"}
                      </button>
                      <button
                        onClick={() => debugFailure(selectedFailure.id)}
                        disabled={debuggingFailure === selectedFailure.id}
                        style={{
                          padding: "6px 12px",
                          background: "var(--accent-primary)",
                          border: "none",
                          borderRadius: "4px",
                          color: "var(--text-primary)",
                          cursor:
                            debuggingFailure === selectedFailure.id
                              ? "wait"
                              : "pointer",
                          fontSize: "12px",
                        }}
                      >
                        {debuggingFailure === selectedFailure.id
                          ? "🔍 Analyzing..."
                          : "🤖 Debug"}
                      </button>
                      {selectedFailure.status !== "fixed" && (
                        <button
                          onClick={() =>
                            updateFailureStatus(selectedFailure.id, "fixed")
                          }
                          style={{
                            padding: "6px 12px",
                            background: "var(--accent-success)",
                            border: "none",
                            borderRadius: "4px",
                            color: "var(--text-primary)",
                            cursor: "pointer",
                            fontSize: "12px",
                          }}
                        >
                          ✅ Mark Fixed
                        </button>
                      )}
                    </div>
                  </div>

                  <div style={{ marginBottom: "15px" }}>
                    <strong style={{ color: "var(--text-secondary)" }}>Timestamp:</strong>{" "}
                    <span style={{ color: "var(--text-primary)" }}>
                      {new Date(selectedFailure.created_at).toLocaleString([], {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}
                    </span>
                  </div>

                  <div style={{ marginBottom: "15px" }}>
                    <strong style={{ color: "var(--text-secondary)" }}>MCP:</strong>{" "}
                    <span style={{ color: "var(--text-primary)" }}>
                      {selectedFailure.mcp_name || selectedFailure.mcp_id}
                    </span>
                  </div>

                  <div style={{ marginBottom: "15px" }}>
                    <strong style={{ color: "var(--text-secondary)" }}>Error Type:</strong>{" "}
                    <span
                      style={{
                        color:
                          selectedFailure.error_type === "timeout"
                            ? "#ff9800"
                            : selectedFailure.error_type === "auth"
                              ? "#f44336"
                              : "#ff6b6b",
                      }}
                    >
                      {selectedFailure.error_type}
                    </span>
                  </div>

                  <div style={{ marginBottom: "15px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <strong style={{ color: "var(--text-secondary)" }}>
                        Error Message:
                      </strong>
                      <button
                        onClick={() =>
                          copyToClipboard(
                            selectedFailure.error_message,
                            "error",
                          )
                        }
                        style={{
                          padding: "2px 8px",
                          background:
                            copiedSection === "error"
                              ? "#4CAF50"
                              : "transparent",
                          border: "1px solid var(--border-primary)",
                          borderRadius: "3px",
                          color: "var(--text-secondary)",
                          cursor: "pointer",
                          fontSize: "11px",
                        }}
                      >
                        {copiedSection === "error" ? "✓" : "📋"}
                      </button>
                    </div>
                    <pre
                      style={{
                        background: "var(--bg-tertiary)",
                        padding: "10px",
                        borderRadius: "4px",
                        color: "var(--accent-danger)",
                        whiteSpace: "pre-wrap",
                        fontSize: "12px",
                        marginTop: "5px",
                      }}
                    >
                      {selectedFailure.error_message}
                    </pre>
                  </div>

                  {selectedFailure.parameters && (
                    <div style={{ marginBottom: "15px" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <strong style={{ color: "var(--text-secondary)" }}>
                          Parameters:
                        </strong>
                        <button
                          onClick={() =>
                            copyToClipboard(
                              typeof selectedFailure.parameters === "string"
                                ? selectedFailure.parameters
                                : JSON.stringify(
                                    selectedFailure.parameters,
                                    null,
                                    2,
                                  ),
                              "params",
                            )
                          }
                          style={{
                            padding: "2px 8px",
                            background:
                              copiedSection === "params"
                                ? "#4CAF50"
                                : "transparent",
                            border: "1px solid var(--border-primary)",
                            borderRadius: "3px",
                            color: "var(--text-secondary)",
                            cursor: "pointer",
                            fontSize: "11px",
                          }}
                        >
                          {copiedSection === "params" ? "✓" : "📋"}
                        </button>
                      </div>
                      <pre
                        style={{
                          background: "var(--bg-tertiary)",
                          padding: "10px",
                          borderRadius: "4px",
                          color: "#4fc3f7",
                          whiteSpace: "pre-wrap",
                          fontSize: "12px",
                          marginTop: "5px",
                          maxHeight: "150px",
                          overflowY: "auto",
                        }}
                      >
                        {typeof selectedFailure.parameters === "string"
                          ? selectedFailure.parameters
                          : JSON.stringify(selectedFailure.parameters, null, 2)}
                      </pre>
                    </div>
                  )}

                  {selectedFailure.user_prompt && (
                    <div style={{ marginBottom: "15px" }}>
                      <strong style={{ color: "var(--text-secondary)" }}>User Prompt:</strong>
                      <div
                        style={{
                          background: "var(--bg-tertiary)",
                          padding: "10px",
                          borderRadius: "4px",
                          color: "var(--text-primary)",
                          marginTop: "5px",
                          fontSize: "13px",
                        }}
                      >
                        {selectedFailure.user_prompt}
                      </div>
                    </div>
                  )}

                  {selectedFailure.stack_trace && (
                    <div style={{ marginBottom: "15px" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <strong style={{ color: "var(--text-secondary)" }}>
                          Stack Trace:
                        </strong>
                        <button
                          onClick={() =>
                            copyToClipboard(
                              selectedFailure.stack_trace,
                              "stack",
                            )
                          }
                          style={{
                            padding: "2px 8px",
                            background:
                              copiedSection === "stack"
                                ? "#4CAF50"
                                : "transparent",
                            border: "1px solid var(--border-primary)",
                            borderRadius: "3px",
                            color: "var(--text-secondary)",
                            cursor: "pointer",
                            fontSize: "11px",
                          }}
                        >
                          {copiedSection === "stack" ? "✓" : "📋"}
                        </button>
                      </div>
                      <pre
                        style={{
                          background: "var(--bg-tertiary)",
                          padding: "10px",
                          borderRadius: "4px",
                          color: "var(--text-secondary)",
                          whiteSpace: "pre-wrap",
                          fontSize: "11px",
                          marginTop: "5px",
                          maxHeight: "150px",
                          overflowY: "auto",
                        }}
                      >
                        {selectedFailure.stack_trace}
                      </pre>
                    </div>
                  )}

                  {selectedFailure.suggested_fix && (
                    <div style={{ marginBottom: "15px" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <strong style={{ color: "var(--accent-primary)" }}>
                          🤖 Suggested Fix:
                        </strong>
                        <button
                          onClick={() =>
                            copyToClipboard(
                              selectedFailure.suggested_fix,
                              "fix",
                            )
                          }
                          style={{
                            padding: "2px 8px",
                            background:
                              copiedSection === "fix"
                                ? "#4CAF50"
                                : "transparent",
                            border: "1px solid #9c27b0",
                            borderRadius: "3px",
                            color: "var(--accent-primary)",
                            cursor: "pointer",
                            fontSize: "11px",
                          }}
                        >
                          {copiedSection === "fix" ? "✓" : "📋"}
                        </button>
                      </div>
                      <div
                        style={{
                          background: "var(--bg-secondary)",
                          padding: "15px",
                          borderRadius: "4px",
                          color: "var(--text-primary)",
                          marginTop: "5px",
                          fontSize: "13px",
                          lineHeight: "1.6",
                          border: "1px solid #9c27b0",
                        }}
                        dangerouslySetInnerHTML={{
                          __html: formatMarkdown(selectedFailure.suggested_fix),
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* MCP MANAGEMENT TAB */}
      {/* ============================================ */}
      {activeAdminTab === "mcps" && (
        <>
          {/* Upload Type Toggle */}
          <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
            <button
              onClick={() => setMcpSubTab("single")}
              style={{
                padding: "8px 16px",
                background: mcpSubTab === "single" ? "#4CAF50" : "var(--bg-tertiary)",
                border: "none",
                borderRadius: "6px",
                color: "var(--text-primary)",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              📄 Single Upload
            </button>
            <button
              onClick={() => setMcpSubTab("bulk")}
              style={{
                padding: "8px 16px",
                background: mcpSubTab === "bulk" ? "#4CAF50" : "var(--bg-tertiary)",
                border: "none",
                borderRadius: "6px",
                color: "var(--text-primary)",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              📦 Bulk Upload
            </button>
          </div>

          {/* Bulk Upload Section */}
          {mcpSubTab === "bulk" && (
            <div className="mcp-admin-section">
              <BulkUploadZone
                onUploadComplete={() => {
                  loadMCPs();
                  loadAuditLogs();
                }}
                showMessage={showMessage}
              />
            </div>
          )}

          {/* Single Upload section */}
          {mcpSubTab === "single" && (
            <div className="mcp-admin-section">
              <h3>📤 Upload New MCP</h3>

              <form onSubmit={handleUpload} className="mcp-upload-form">
                {/* File uploads */}
                <div className="mcp-upload-files">
                  {/* server.py */}
                  <div
                    className="mcp-upload-zone"
                    onDrop={(e) => handleFileDrop(e, "serverPyFile")}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <input
                      type="file"
                      id="serverPyFile"
                      accept=".py"
                      onChange={(e) => handleFileSelect(e, "serverPyFile")}
                      style={{ display: "none" }}
                    />
                    <label htmlFor="serverPyFile" className="mcp-upload-label">
                      {uploadForm.serverPyFile ? (
                        <>
                          <span className="mcp-file-icon">📄</span>
                          <span className="mcp-file-name">
                            {uploadForm.serverPyFile.name}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="mcp-upload-icon">📥</span>
                          <span>Drop server.py or click to select</span>
                        </>
                      )}
                    </label>
                  </div>

                  {/* requirements.txt (recommended) */}
                  <div
                    className="mcp-upload-zone"
                    onDrop={(e) => handleFileDrop(e, "requirementsFile")}
                    onDragOver={(e) => e.preventDefault()}
                  >
                    <input
                      type="file"
                      id="requirementsFile"
                      accept=".txt"
                      onChange={(e) => handleFileSelect(e, "requirementsFile")}
                      style={{ display: "none" }}
                    />
                    <label
                      htmlFor="requirementsFile"
                      className="mcp-upload-label"
                    >
                      {uploadForm.requirementsFile ? (
                        <>
                          <span className="mcp-file-icon">📄</span>
                          <span className="mcp-file-name">
                            {uploadForm.requirementsFile.name}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="mcp-upload-icon">📥</span>
                          <span>requirements.txt</span>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                {/* Metadata fields */}
                <div className="mcp-upload-metadata">
                  <div className="mcp-form-row">
                    <div className="mcp-form-field">
                      <label>Name (internal)</label>
                      <input
                        type="text"
                        placeholder="e.g., apollo_io"
                        value={uploadForm.name}
                        onChange={(e) =>
                          setUploadForm({ ...uploadForm, name: e.target.value })
                        }
                        required
                      />
                    </div>

                    <div className="mcp-form-field">
                      <label>Display Name</label>
                      <input
                        type="text"
                        placeholder="e.g., Apollo.io"
                        value={uploadForm.displayName}
                        onChange={(e) =>
                          setUploadForm({
                            ...uploadForm,
                            displayName: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                  </div>

                  <div className="mcp-form-row">
                    <div className="mcp-form-field">
                      <label>Category</label>
                      <select
                        value={uploadForm.category}
                        onChange={(e) => {
                          const newCategory = e.target.value;
                          setUploadForm({
                            ...uploadForm,
                            category: newCategory,
                            icon: categoryEmojis[newCategory] || "🔌",
                          });
                        }}
                      >
                        <option value="sales">Sales</option>
                        <option value="crm">CRM</option>
                        <option value="marketing">Marketing</option>
                        <option value="automation">Automation</option>
                        <option value="analytics">Analytics</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div className="mcp-form-field">
                      <label>Icon (auto-selected)</label>
                      <input
                        type="text"
                        placeholder="🔌"
                        value={uploadForm.icon}
                        onChange={(e) =>
                          setUploadForm({ ...uploadForm, icon: e.target.value })
                        }
                        maxLength={2}
                        style={{
                          width: "60px",
                          textAlign: "center",
                          fontSize: "20px",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "11px",
                          color: "var(--text-tertiary)",
                          marginTop: "4px",
                          display: "block",
                        }}
                      >
                        Changes with category
                      </span>
                    </div>
                  </div>

                  <div className="mcp-form-field">
                    <label>Description</label>
                    <textarea
                      placeholder="What does this MCP do?"
                      value={uploadForm.description}
                      onChange={(e) =>
                        setUploadForm({
                          ...uploadForm,
                          description: e.target.value,
                        })
                      }
                      rows={3}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="mcp-upload-button"
                  disabled={uploading}
                >
                  {uploading ? "⏳ Uploading..." : "📤 Upload MCP"}
                </button>
              </form>
            </div>
          )}

          {/* MCP list */}
          <div className="mcp-admin-section">
            <h3>📋 Installed MCPs</h3>

            {/* Search Bar */}
            <div style={{ marginBottom: "15px" }}>
              <input
                type="text"
                placeholder="🔍 Search MCPs by name, ID, or category..."
                value={mcpSearch}
                onChange={(e) => setMcpSearch(e.target.value)}
                style={{
                  width: "100%",
                  maxWidth: "400px",
                  padding: "10px 15px",
                  background: "var(--bg-tertiary)",
                  border: "1px solid var(--border-primary)",
                  borderRadius: "6px",
                  color: "var(--text-primary)",
                  fontSize: "14px",
                }}
              />
            </div>

            {loading ? (
              <div className="mcp-loading">Loading MCPs...</div>
            ) : mcps.length === 0 ? (
              <div className="mcp-empty">
                No MCPs uploaded yet. Upload your first MCP above!
              </div>
            ) : (
              <div className="mcp-list">
                {mcps
                  .filter((mcp) => {
                    if (!mcpSearch.trim()) return true;
                    const search = mcpSearch.toLowerCase();
                    return (
                      (mcp.display_name || "").toLowerCase().includes(search) ||
                      (mcp.name || "").toLowerCase().includes(search) ||
                      (mcp.id || "").toLowerCase().includes(search) ||
                      (mcp.category || "").toLowerCase().includes(search) ||
                      (mcp.description || "").toLowerCase().includes(search)
                    );
                  })
                  .map((mcp) => (
                    <div key={mcp.id} className="mcp-card">
                      <div className="mcp-card-header">
                        <div className="mcp-card-title">
                          <span className="mcp-icon">{mcp.icon || "🔌"}</span>
                          <span className="mcp-name">{mcp.display_name}</span>
                          {getStatusBadge(mcp.status)}
                          <span
                            className="mcp-version-badge"
                            onClick={() => openVersionHistory(mcp)}
                            style={{ cursor: "pointer" }}
                          >
                            v{mcp.version}
                          </span>
                        </div>
                        <div className="mcp-card-actions">
                          {(mcp.status === "draft" ||
                            mcp.status === "available") && (
                            <>
                              <button
                                onClick={() => openCodeViewer(mcp)}
                                className="mcp-btn-secondary"
                                title="View deployed code"
                              >
                                👁️ Code
                              </button>
                              <button
                                onClick={() => openTestModal(mcp)}
                                className="mcp-btn-secondary"
                                disabled={testing[mcp.id]}
                              >
                                {testing[mcp.id] ? "⏳" : "🧪"} Test
                              </button>
                            </>
                          )}
                          {mcp.status === "draft" && (
                            <button
                              onClick={() => handlePublish(mcp.id)}
                              className="mcp-btn-primary"
                              disabled={publishing[mcp.id]}
                            >
                              {publishing[mcp.id] ? "⏳" : "🚀"} Publish
                            </button>
                          )}
                          <button
                            onClick={() =>
                              handleDelete(mcp.id, mcp.display_name)
                            }
                            className="mcp-btn-danger"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>

                      <div className="mcp-card-body">
                        <p className="mcp-description">
                          {mcp.description || "No description"}
                        </p>

                        <div className="mcp-metadata">
                          <span
                            className="mcp-meta-item mcp-id-badge"
                            title="Click to copy MCP ID"
                            onClick={() => {
                              navigator.clipboard.writeText(mcp.id);
                              showMessage("success", "MCP ID copied!");
                            }}
                            style={{
                              cursor: "pointer",
                              fontFamily: "monospace",
                            }}
                          >
                            <strong>ID:</strong> {mcp.id}
                          </span>
                          <span className="mcp-meta-item">
                            <strong>Tools:</strong> {mcp.tool_count}
                          </span>
                          <span className="mcp-meta-item">
                            <strong>Category:</strong> {mcp.category || "N/A"}
                          </span>
                          <span
                            className="mcp-meta-item"
                            onClick={() => openVersionHistory(mcp)}
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                          >
                            <strong>Version:</strong> v{mcp.version} (click for
                            history)
                          </span>
                          <span className="mcp-meta-item">
                            <strong>Created:</strong>{" "}
                            {formatDate(mcp.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Audit logs */}
          <div className="mcp-admin-section">
            <h3>📜 Activity Log</h3>

            {auditLogs.length === 0 ? (
              <div className="mcp-empty">No activity yet</div>
            ) : (
              <div className="mcp-audit-list">
                {auditLogs.slice(0, 10).map((log, index) => (
                  <div key={index} className="mcp-audit-item">
                    <span className="mcp-audit-time">
                      {formatDate(log.created_at)}
                    </span>
                    <span className="mcp-audit-action">{log.action}</span>
                    <span className="mcp-audit-resource">
                      {log.resource_id}
                    </span>
                    <span
                      className={`mcp-audit-status ${log.success ? "success" : "failed"}`}
                    >
                      {log.success ? "✅" : "❌"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Kill Switch Modal */}
      {killModalOpen && (
        <div
          className="mcp-modal-overlay"
          onClick={() => setKillModalOpen(false)}
        >
          <div className="mcp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              {killTarget.type === "user" ? "👤" : "🔌"} Kill Switch:{" "}
              {killTarget.name || "Enter ID"}
            </h3>

            <div className="mcp-form-field" style={{ marginBottom: "15px" }}>
              <label>
                Target {killTarget.type === "user" ? "User" : "MCP"} ID
              </label>
              <input
                type="text"
                value={killTarget.id}
                onChange={(e) =>
                  setKillTarget({
                    ...killTarget,
                    id: e.target.value,
                    name: e.target.value,
                  })
                }
                placeholder={`Enter ${killTarget.type} ID`}
                style={{
                  width: "100%",
                  padding: "10px",
                  background: "var(--bg-tertiary)",
                  border: "1px solid var(--border-primary)",
                  borderRadius: "4px",
                  color: "var(--text-primary)",
                }}
              />
            </div>

            <div className="mcp-form-field" style={{ marginBottom: "15px" }}>
              <label>Reason (required)</label>
              <textarea
                value={killForm.reason}
                onChange={(e) =>
                  setKillForm({ ...killForm, reason: e.target.value })
                }
                placeholder="Why are you taking this action?"
                rows={3}
                style={{
                  width: "100%",
                  padding: "10px",
                  background: "var(--bg-tertiary)",
                  border: "1px solid var(--border-primary)",
                  borderRadius: "4px",
                  color: "var(--text-primary)",
                  resize: "vertical",
                }}
              />
            </div>

            <div className="mcp-form-field" style={{ marginBottom: "20px" }}>
              <label>Duration (hours, leave empty for permanent)</label>
              <input
                type="number"
                value={killForm.duration_hours}
                onChange={(e) =>
                  setKillForm({ ...killForm, duration_hours: e.target.value })
                }
                placeholder="e.g., 24 for 24 hours"
                style={{
                  width: "100%",
                  padding: "10px",
                  background: "var(--bg-tertiary)",
                  border: "1px solid var(--border-primary)",
                  borderRadius: "4px",
                  color: "var(--text-primary)",
                }}
              />
            </div>

            <div
              className="mcp-modal-actions"
              style={{ display: "flex", gap: "10px" }}
            >
              <button
                onClick={() => setKillModalOpen(false)}
                className="mcp-btn-secondary"
                style={{
                  flex: 1,
                  padding: "10px",
                  background: "var(--border-primary)",
                  border: "none",
                  borderRadius: "4px",
                  color: "var(--text-primary)",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleKillAction("quarantine")}
                disabled={killLoading || !killTarget.id}
                style={{
                  flex: 1,
                  padding: "10px",
                  background: "var(--accent-warning)",
                  border: "none",
                  borderRadius: "4px",
                  color: "var(--text-primary)",
                  cursor:
                    killLoading || !killTarget.id ? "not-allowed" : "pointer",
                  opacity: killLoading || !killTarget.id ? 0.5 : 1,
                }}
              >
                ⚠️ Quarantine
              </button>
              <button
                onClick={() => handleKillAction("kill")}
                disabled={killLoading || !killTarget.id}
                style={{
                  flex: 1,
                  padding: "10px",
                  background: "var(--accent-danger)",
                  border: "none",
                  borderRadius: "4px",
                  color: "var(--text-primary)",
                  cursor:
                    killLoading || !killTarget.id ? "not-allowed" : "pointer",
                  opacity: killLoading || !killTarget.id ? 0.5 : 1,
                }}
              >
                {killLoading ? "⏳" : "🔴"} Kill
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test credentials modal */}
      {testModal.open && (
        <div
          className="mcp-modal-overlay"
          onClick={() => setTestModal({ ...testModal, open: false })}
        >
          <div className="mcp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>🧪 Test {testModal.mcpName}</h3>

            <p>Enter credentials to test this MCP:</p>

            {testModal.envVars.length === 0 ? (
              <p className="mcp-no-credentials">No credentials required</p>
            ) : (
              <div className="mcp-credentials-form">
                {testModal.envVars.map((envVar) => (
                  <div key={envVar} className="mcp-form-field">
                    <label>{envVar}</label>
                    <input
                      type="password"
                      placeholder={`Enter ${envVar}`}
                      value={testModal.credentials[envVar] || ""}
                      onChange={(e) =>
                        setTestModal({
                          ...testModal,
                          credentials: {
                            ...testModal.credentials,
                            [envVar]: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="mcp-modal-actions">
              <button
                onClick={() => setTestModal({ ...testModal, open: false })}
                className="mcp-btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleTest}
                className="mcp-btn-primary"
                disabled={testing[testModal.mcpId]}
              >
                {testing[testModal.mcpId] ? (
                  <span>
                    Testing... {testProgress.percent}%
                    {testProgress.stage && (
                      <span
                        style={{
                          display: "block",
                          fontSize: "0.8em",
                          marginTop: "4px",
                        }}
                      >
                        {testProgress.stage}
                      </span>
                    )}
                  </span>
                ) : (
                  "Run Test"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Version history modal */}
      {versionModal.open && (
        <div
          className="mcp-modal-overlay"
          onClick={() => setVersionModal({ ...versionModal, open: false })}
        >
          <div className="mcp-modal" onClick={(e) => e.stopPropagation()}>
            <h3>📚 Version History: {versionModal.mcpName}</h3>

            {versionModal.loading ? (
              <div className="mcp-loading">Loading versions...</div>
            ) : versionModal.versions.length === 0 ? (
              <p>No version history available</p>
            ) : (
              <div className="mcp-version-list">
                {versionModal.versions.map((version) => (
                  <div
                    key={version.version}
                    className={`mcp-version-item ${version.is_current ? "current" : ""}`}
                  >
                    <div className="mcp-version-header">
                      <span className="mcp-version-number">
                        v{version.version}
                        {version.is_current && (
                          <span className="mcp-version-current-badge">
                            {" "}
                            (Current)
                          </span>
                        )}
                      </span>
                      <span className="mcp-version-date">
                        {formatDate(version.created_at)}
                      </span>
                    </div>
                    <div className="mcp-version-details">
                      <p>
                        <strong>Uploaded by:</strong>{" "}
                        {version.uploaded_by || "Unknown"}
                      </p>
                      {version.changelog && (
                        <p>
                          <strong>Changes:</strong> {version.changelog}
                        </p>
                      )}
                    </div>
                    {!version.is_current && (
                      <button
                        onClick={() =>
                          handleRollback(versionModal.mcpId, version.version)
                        }
                        className="mcp-btn-secondary mcp-btn-small"
                      >
                        ↩️ Rollback to v{version.version}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mcp-modal-actions">
              <button
                onClick={() =>
                  setVersionModal({ ...versionModal, open: false })
                }
                className="mcp-btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Code modal */}
      {codeModal.open && (
        <div
          className="mcp-modal-overlay"
          onClick={() => setCodeModal({ ...codeModal, open: false })}
        >
          <div
            className="mcp-modal mcp-modal-wide"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>👁️ Deployed Code: {codeModal.mcpName}</h3>

            {codeModal.loading ? (
              <div className="mcp-loading">Loading code...</div>
            ) : (
              <>
                <div className="mcp-code-info">
                  <span>
                    <strong>MCP ID:</strong> {codeModal.mcpId}
                  </span>
                  <span>
                    <strong>Version:</strong> v{codeModal.version}
                  </span>
                  <span>
                    <strong>Lines:</strong> {codeModal.lines}
                  </span>
                </div>
                <div className="mcp-code-info mcp-code-debug">
                  <span>
                    <strong>📁 Source:</strong>{" "}
                    {codeModal.sourcePath || "Unknown"}
                  </span>
                  <span>
                    <strong>👤 Uploaded by:</strong>{" "}
                    {codeModal.uploadedBy || "Unknown"}
                  </span>
                  {codeModal.uploadedAt && (
                    <span>
                      <strong>📅 Uploaded:</strong>{" "}
                      {new Date(codeModal.uploadedAt).toLocaleString()}
                    </span>
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h4>server.py</h4>
                  {!codeModal.editing && (
                    <button
                      onClick={handleCodeEdit}
                      className="mcp-btn-secondary mcp-btn-small"
                      title="Edit code (creates new version)"
                    >
                      ✏️ Edit
                    </button>
                  )}
                </div>

                {codeModal.editing ? (
                  useCodeMirror ? (
                    <CodeMirrorEditor
                      value={codeModal.editedServerPy}
                      onChange={(val) =>
                        setCodeModal((prev) => ({
                          ...prev,
                          editedServerPy: val,
                        }))
                      }
                      height="400px"
                      readOnly={false}
                    />
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        backgroundColor: "#1a1a2e",
                        borderRadius: "4px",
                        border: "1px solid var(--border-primary)",
                      }}
                    >
                      {/* Line numbers */}
                      <div
                        style={{
                          padding: "12px 8px",
                          fontFamily: "monospace",
                          fontSize: "13px",
                          color: "var(--text-tertiary)",
                          backgroundColor: "#151525",
                          borderRight: "1px solid var(--border-primary)",
                          textAlign: "right",
                          userSelect: "none",
                          minWidth: "45px",
                        }}
                      >
                        {codeModal.editedServerPy.split("\n").map((_, i) => (
                          <div key={i}>{i + 1}</div>
                        ))}
                      </div>
                      <textarea
                        value={codeModal.editedServerPy}
                        onChange={(e) =>
                          setCodeModal((prev) => ({
                            ...prev,
                            editedServerPy: e.target.value,
                          }))
                        }
                        className="mcp-code-textarea"
                        style={{
                          flex: 1,
                          minHeight: "400px",
                          fontFamily: "monospace",
                          fontSize: "13px",
                          padding: "12px",
                          backgroundColor: "transparent",
                          color: "#e0e0e0",
                          border: "none",
                          borderRadius: "0",
                          resize: "vertical",
                          outline: "none",
                          lineHeight: "1.4",
                        }}
                      />
                    </div>
                  )
                ) : (
                  <pre className="mcp-code-block">
                    <code>{codeModal.serverPy || "No server.py content"}</code>
                  </pre>
                )}

                <h4>requirements.txt</h4>
                {codeModal.editing ? (
                  <textarea
                    value={codeModal.editedRequirements}
                    onChange={(e) =>
                      setCodeModal((prev) => ({
                        ...prev,
                        editedRequirements: e.target.value,
                      }))
                    }
                    className="mcp-code-textarea"
                    style={{
                      width: "100%",
                      minHeight: "100px",
                      fontFamily: "monospace",
                      fontSize: "13px",
                      padding: "12px",
                      backgroundColor: "#1a1a2e",
                      color: "#e0e0e0",
                      border: "1px solid var(--border-primary)",
                      borderRadius: "4px",
                      resize: "vertical",
                    }}
                    placeholder="numpy==1.24.0&#10;requests==2.31.0"
                  />
                ) : (
                  <pre className="mcp-code-block mcp-code-small">
                    <code>{codeModal.requirements || "No requirements"}</code>
                  </pre>
                )}

                {codeModal.editing && (
                  <div
                    style={{ marginTop: "16px", display: "flex", gap: "8px" }}
                  >
                    <button
                      onClick={handleCodeSave}
                      disabled={codeModal.saving}
                      className="mcp-btn-primary"
                    >
                      {codeModal.saving ? "Saving..." : "💾 Save (New Version)"}
                    </button>
                    <button
                      onClick={handleCodeEditCancel}
                      disabled={codeModal.saving}
                      className="mcp-btn-secondary"
                    >
                      Cancel
                    </button>
                    <span
                      style={{
                        color: "#f5a623",
                        fontSize: "12px",
                        alignSelf: "center",
                      }}
                    >
                      ⚠️ Saving creates a new version and sets MCP to draft
                    </span>
                  </div>
                )}
              </>
            )}

            {/* Replace Code & Requirements Section */}
            <div
              style={{
                marginTop: "24px",
                paddingTop: "24px",
                borderTop: "1px solid var(--border-primary)",
              }}
            >
              <h4>🔄 Replace Code & Requirements</h4>
              <div className="mcp-upload-files">
                {/* server.py */}
                <div
                  className="mcp-upload-zone"
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file)
                      setCodeModal({ ...codeModal, replaceServerPy: file });
                  }}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <input
                    type="file"
                    id="replaceServerPyFile"
                    accept=".py"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file)
                        setCodeModal({ ...codeModal, replaceServerPy: file });
                    }}
                    style={{ display: "none" }}
                  />
                  <label
                    htmlFor="replaceServerPyFile"
                    className="mcp-upload-label"
                  >
                    {codeModal.replaceServerPy ? (
                      <>
                        <span className="mcp-file-icon">📄</span>
                        <span className="mcp-file-name">
                          {codeModal.replaceServerPy.name}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="mcp-upload-icon">📥</span>
                        <span>Drop server.py or click to select</span>
                      </>
                    )}
                  </label>
                </div>

                {/* requirements.txt */}
                <div
                  className="mcp-upload-zone"
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files[0];
                    if (file)
                      setCodeModal({ ...codeModal, replaceRequirements: file });
                  }}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <input
                    type="file"
                    id="replaceRequirementsFile"
                    accept=".txt"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file)
                        setCodeModal({
                          ...codeModal,
                          replaceRequirements: file,
                        });
                    }}
                    style={{ display: "none" }}
                  />
                  <label
                    htmlFor="replaceRequirementsFile"
                    className="mcp-upload-label"
                  >
                    {codeModal.replaceRequirements ? (
                      <>
                        <span className="mcp-file-icon">📄</span>
                        <span className="mcp-file-name">
                          {codeModal.replaceRequirements.name}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="mcp-upload-icon">📥</span>
                        <span>requirements.txt</span>
                      </>
                    )}
                  </label>
                </div>
              </div>

              <button
                onClick={async () => {
                  if (
                    !codeModal.replaceServerPy ||
                    !codeModal.replaceRequirements
                  ) {
                    showMessage(
                      "error",
                      "Please select both server.py and requirements.txt",
                    );
                    return;
                  }

                  setCodeModal({ ...codeModal, replacing: true });

                  try {
                    const mcp = codeModal.mcp;
                    const formData = new FormData();
                    formData.append("server_py", codeModal.replaceServerPy);
                    formData.append(
                      "requirements",
                      codeModal.replaceRequirements,
                    );
                    formData.append("name", mcp.name);
                    formData.append("display_name", mcp.display_name);
                    formData.append("description", mcp.description || "");
                    formData.append("category", mcp.category || "other");
                    formData.append("icon", mcp.icon || "🔌");

                    const response = await authFetch(
                      apiUrl("/api/mcp/upload"),
                      {
                        method: "POST",
                        body: formData,
                      },
                    );
                    const data = await response.json();

                    if (!response.ok || data.error) {
                      const errorMsg =
                        data.error?.issues?.join(", ") ||
                        data.error ||
                        data.detail?.error ||
                        "Replace failed";
                      showMessage("error", errorMsg);
                      return;
                    }

                    showMessage(
                      "success",
                      `Code & requirements replaced successfully!`,
                    );
                    setCodeModal({ ...codeModal, open: false });
                    await loadMCPs();
                    await loadAuditLogs();
                  } catch (error) {
                    console.error("Replace failed:", error);
                    showMessage("error", "Replace failed");
                  } finally {
                    setCodeModal((prev) => ({ ...prev, replacing: false }));
                  }
                }}
                className="mcp-btn-primary"
                disabled={
                  codeModal.replacing ||
                  !codeModal.replaceServerPy ||
                  !codeModal.replaceRequirements
                }
                style={{ marginTop: "12px" }}
              >
                {codeModal.replacing
                  ? "⏳ Replacing..."
                  : "🔄 Replace Code & Requirements"}
              </button>
            </div>

            <div className="mcp-modal-actions">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(codeModal.serverPy);
                  showMessage("success", "Code copied to clipboard");
                }}
                className="mcp-btn-primary"
                disabled={codeModal.loading}
              >
                📋 Copy Code
              </button>
              <button
                onClick={() => setCodeModal({ ...codeModal, open: false })}
                className="mcp-btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Results Modal - Persistent until closed */}
      {testResults.open && (
        <div
          className="mcp-modal-overlay"
          onClick={() => setTestResults({ ...testResults, open: false })}
        >
          <div className="mcp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mcp-modal-header">
              <h3>
                {testResults.success ? "✅ " : "❌ "}
                Test Results: {testResults.mcpName}
              </h3>
              <button
                onClick={() => setTestResults({ ...testResults, open: false })}
                className="mcp-modal-close"
              >
                ✕
              </button>
            </div>

            <div className="mcp-modal-content">
              <div style={{ marginBottom: "20px" }}>
                <strong>Duration:</strong>{" "}
                {(testResults.duration / 1000).toFixed(2)}s
              </div>

              {testResults.success ? (
                <>
                  <div style={{ marginBottom: "20px" }}>
                    <strong>✅ Success!</strong> MCP process started and tools
                    discovered.
                  </div>

                  <div style={{ marginBottom: "10px" }}>
                    <strong>Tools Discovered:</strong>{" "}
                    {testResults.tools.length}
                  </div>

                  {testResults.tools.length > 0 && (
                    <div
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        border: "1px solid var(--border-primary)",
                        borderRadius: "4px",
                        padding: "10px",
                        backgroundColor: "var(--bg-secondary)",
                      }}
                    >
                      {testResults.tools.map((tool, idx) => (
                        <div
                          key={idx}
                          style={{
                            marginBottom: "15px",
                            paddingBottom: "15px",
                            borderBottom:
                              idx < testResults.tools.length - 1
                                ? "1px solid var(--border-primary)"
                                : "none",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "bold",
                              color: "var(--accent-success)",
                              marginBottom: "5px",
                            }}
                          >
                            {tool.name}
                          </div>
                          <div style={{ fontSize: "0.9em", color: "var(--text-secondary)" }}>
                            {tool.description || "No description"}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div
                    style={{
                      marginTop: "20px",
                      padding: "10px",
                      backgroundColor: "#1a4d1a",
                      borderRadius: "4px",
                    }}
                  >
                    <strong>Next steps:</strong>
                    <ol style={{ marginTop: "10px", paddingLeft: "20px" }}>
                      <li>Tools have been cached to database</li>
                      <li>MCP is ready to publish</li>
                      <li>
                        After publishing, add credentials in Integrations tab
                      </li>
                    </ol>
                  </div>
                </>
              ) : (
                <>
                  <div
                    style={{
                      marginBottom: "20px",
                      padding: "15px",
                      backgroundColor: "#4d1a1a",
                      borderRadius: "4px",
                    }}
                  >
                    <strong>❌ Test Failed</strong>
                  </div>

                  <div style={{ marginBottom: "10px" }}>
                    <strong>Error Details:</strong>
                  </div>
                  <div
                    style={{
                      padding: "15px",
                      backgroundColor: "var(--bg-secondary)",
                      borderRadius: "4px",
                      border: "1px solid #d32f2f",
                      fontFamily: "monospace",
                      fontSize: "0.9em",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {testResults.error}
                  </div>

                  <div
                    style={{
                      marginTop: "20px",
                      padding: "10px",
                      backgroundColor: "var(--bg-secondary)",
                      borderRadius: "4px",
                    }}
                  >
                    <strong>Common Issues:</strong>
                    <ul
                      style={{
                        marginTop: "10px",
                        paddingLeft: "20px",
                        fontSize: "0.9em",
                      }}
                    >
                      <li>
                        <strong>Failed to start process:</strong> Gateway
                        resource limits or Python environment issue
                      </li>
                      <li>
                        <strong>Timeout:</strong> MCP dependencies taking too
                        long to install
                      </li>
                      <li>
                        <strong>Invalid credentials:</strong> Check API key
                        format and permissions
                      </li>
                      <li>
                        <strong>Import errors:</strong> Missing dependencies in
                        requirements.txt
                      </li>
                    </ul>
                  </div>

                  <div
                    style={{
                      marginTop: "20px",
                      padding: "10px",
                      backgroundColor: "#1a3d4d",
                      borderRadius: "4px",
                    }}
                  >
                    <strong>Debugging steps:</strong>
                    <ol
                      style={{
                        marginTop: "10px",
                        paddingLeft: "20px",
                        fontSize: "0.9em",
                      }}
                    >
                      <li>Check Railway Gateway logs for Python errors</li>
                      <li>Verify MCP server.py has no syntax errors</li>
                      <li>
                        Test requirements.txt locally:{" "}
                        <code>pip install -r requirements.txt</code>
                      </li>
                      <li>Contact support if issue persists</li>
                    </ol>
                  </div>
                </>
              )}
            </div>

            <div className="mcp-modal-footer">
              <button
                onClick={() => setTestResults({ ...testResults, open: false })}
                className="mcp-btn-primary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MCPAdminTab;
